<template>
	<section class="dp-layout exam-management-layout">
		<!-- 左侧组织架构树 -->
		<section class="dp-left">
			<DepartmentPane directory-scope="school" @tree-node-click="treeNodeClick" :type="1"></DepartmentPane>
		</section>

		<!-- 右侧核心工作区（与人员信息管理等页面结构一致） -->
		<section class="dp-right">
			<toolbar :buttonList="buttonList" @callFunction="callFunction" :searchList="searchList"></toolbar>
			<div class="tbl-scroll-host">
			<el-table :data="exams" v-loading="listLoading" ref="table" class="modern-table custom-tbl" height="100%" style="width: 100%">
					<el-table-column prop="ExamName" label="考试名称" min-width="140">
						<template slot-scope="scope">
							<span class="exam-name-text">{{ scope.row.ExamName }}</span>
						</template>
					</el-table-column>
					<el-table-column prop="DepartmentName" label="所属部门" min-width="120"></el-table-column>
					<el-table-column label="考试人员范围" min-width="170">
						<template slot-scope="scope">
							<el-button
								v-if="parseExamDeptNames(scope.row.DeptNames).length"
								type="text"
								size="small"
								icon="el-icon-office-building"
								class="kq-dept-link-btn"
								@click.stop="showAllDeptNames(scope.row)">
								已选 {{ parseExamDeptNames(scope.row.DeptNames).length }} 个班级/部门
							</el-button>
							<span v-else class="ac-empty-dash">-</span>
						</template>
					</el-table-column>
					<el-table-column prop="ExamType" label="类型" width="100" align="center">
						<template slot-scope="scope">
							<el-tag size="small" type="success" class="type-tag">{{ scope.row.ExamType || '普通考试' }}</el-tag>
						</template>
					</el-table-column>
					<el-table-column label="状态" width="90" align="center">
						<template slot-scope="scope">
							<el-tag size="small" :type="getExamStatusType(scope.row)">{{ getExamStatusText(scope.row) }}</el-tag>
						</template>
					</el-table-column>
					<el-table-column label="起止时间" width="180" align="center">
						<template slot-scope="scope">
							<div class="time-range-display">
								<span class="time-start">{{ formatDate(scope.row.StartTime) }}</span>
								<span class="time-sep">至</span>
								<span class="time-end">{{ formatDate(scope.row.EndTime) }}</span>
							</div>
						</template>
					</el-table-column>
					<el-table-column prop="SessionCount" label="场次" width="80" align="center">
						<template slot-scope="scope">
							<span class="session-badge">{{ scope.row.SessionCount || 0 }} 场</span>
						</template>
					</el-table-column>
					<el-table-column label="考试编排概览" width="220" align="center">
						<template slot-scope="scope">
							<div class="mini-stats-grid">
								<div class="mini-stat-item">
									<span class="mini-stat-label">考场数</span>
									<span class="mini-stat-val">{{ scope.row.RoomCount || 0 }}</span>
								</div>
								<div class="mini-stat-item">
									<span class="mini-stat-label">考生数</span>
									<span class="mini-stat-val">{{ scope.row.StudentCount || 0 }}</span>
								</div>
								<div class="mini-stat-item highlight">
									<span class="mini-stat-label">已编排</span>
									<span class="mini-stat-val">{{ scope.row.AssignedCount || 0 }}</span>
								</div>
								<div class="mini-stat-item warning">
									<span class="mini-stat-label">未编排</span>
									<span class="mini-stat-val">{{ scope.row.UnassignedCount || 0 }}</span>
								</div>
							</div>
						</template>
					</el-table-column>
					<el-table-column fixed="right" label="操作" width="200" align="center">
						<template slot-scope="scope">
							<div class="table-op-pair">
								<el-tooltip :disabled="canEditExamInfo(scope.row)" content="考试进行中或已结束，不可编辑" placement="top">
									<span>
										<el-button type="text" class="table-op-link" :disabled="!canEditExamInfo(scope.row)" @click="handleEdit(scope.row)">编辑</el-button>
									</span>
								</el-tooltip>
								<el-tooltip :disabled="canDeleteExam(scope.row)" content="考试进行中或已结束，不可删除" placement="top">
									<span>
										<el-button type="text" class="table-op-link table-op-link--danger" :disabled="!canDeleteExam(scope.row)" @click="handleDelete(scope.row.Id)">删除</el-button>
									</span>
								</el-tooltip>
								<el-button type="text" class="table-op-link" @click="showExamSessions(scope.row)">场次管理</el-button>
								<el-button type="text" class="table-op-link" @click="showExamStudent(scope.row)">座位图</el-button>
								<el-tooltip content="跳过状态与场次限制，需超密授权。" placement="top">
									<el-button type="text" class="table-op-link table-op-link--danger" @click="openForceDeleteDialog(scope.row)">强制删除</el-button>
								</el-tooltip>
							</div>
						</template>
					</el-table-column>
				</el-table>
			</div>

			<!-- 分页工具条 -->
			<el-col :span="24" class="toolbar">
				<el-pagination layout="total, prev, pager, next" @current-change="handleCurrentChange"
					:page-size="pageSize" :total="total" style="float: right"></el-pagination>
			</el-col>

			<!-- 1. 新增/编辑考试 Dialog -->
			<el-dialog :title="dialogTitle" :visible.sync="dialogVisible" :close-on-click-modal="false"
				custom-class="glass-modal" width="560px" @close="handleDialogClose">
				<el-form :model="formData" label-width="100px" :rules="formRules" ref="formRef" class="modern-form">
					<el-form-item label="考试名称" prop="ExamName">
						<el-input v-model="formData.ExamName" placeholder="请录入本次考试名称" auto-complete="off"></el-input>
					</el-form-item>

					<el-form-item label="考试类型" prop="ExamType">
						<el-input v-model="formData.ExamType" placeholder="例如：期中考试、期末统考" auto-complete="off"></el-input>
					</el-form-item>

					<el-form-item label="参与人员" prop="DeptIds" v-if="allOptions && allOptions.length > 0">
						<el-cascader style="width: 100%" v-model="formData.DeptIds" :show-all-levels="false"
							placeholder="选择允许参加此考试的班级或部门"
							:options="allOptions" :key="isResouceShow" :props="{ multiple: true }"
							clearable></el-cascader>
					</el-form-item>

					<el-form-item label="起止时间" prop="timeRange">
						<el-date-picker
							:key="examTimePickerKey"
							v-model="formData.timeRange"
							type="datetimerange"
							start-placeholder="考试开始时间"
							end-placeholder="考试结束时间"
							format="yyyy-MM-dd HH:mm:ss"
							value-format="yyyy-MM-dd HH:mm:ss"
							:default-time="['00:00:00', '23:59:59']"
							popper-append-to-body
							@change="handleTimeChange"
							style="width: 100%" />
					</el-form-item>

					<el-form-item label="备注信息">
						<el-input v-model="formData.Remark" type="textarea" :rows="3" placeholder="添加备注或说明..."></el-input>
					</el-form-item>
				</el-form>
				<div slot="footer" class="dialog-footer">
					<el-button @click.native="dialogVisible = false">取消</el-button>
					<el-button type="primary" @click.native="submitForm" :loading="submitLoading" class="btn-submit">
						{{ isEditMode ? '更新考试' : '提交考试' }}
					</el-button>
				</div>
			</el-dialog>

			<!-- 2. 考试场次管理 Dialog (采用大视区弹窗展现多场次) -->
			<el-dialog :title="examSessionDialogTitle" :visible.sync="examSessionDialogVisible"
				custom-class="glass-modal width-large" :close-on-click-modal="false" width="55%"
				:append-to-body="true">
				<div class="session-management-box">
					<el-alert
						v-if="currentExam && currentExam.Id && !canEditExamInfo(currentExam)"
						type="warning"
						:closable="false"
						show-icon
						class="exam-lifecycle-alert"
						:title="`当前考试${getExamStatusText(currentExam)}，仅可查看场次与签到记录，不可修改考试及场次编排信息。`" />
					<div class="box-header-row" style="display: flex; justify-content: space-between; align-items: center; width: 100%;">
						<div class="exam-title-badge">
							<span class="dot"></span>
							当前考试：{{ currentExam.ExamName }}
							<el-tag v-if="currentExam && currentExam.Id" size="mini" :type="getExamStatusType(currentExam)" style="margin-left:8px">
								{{ getExamStatusText(currentExam) }}
							</el-tag>
						</div>
						<div class="action-buttons-group" style="display: flex; align-items: center; gap: 10px;">
							<el-tooltip :disabled="canManageExamArrangement(currentExam)" content="考试进行中或已结束，不可编排" placement="top">
								<span>
									<el-button type="success" size="small" icon="el-icon-school" :disabled="!canManageExamArrangement(currentExam)" @click="handleGlobalRoomAssignment">
										考场与考生编排
									</el-button>
								</span>
							</el-tooltip>
							<el-tooltip :disabled="canAddExamSession(currentExam)" content="考试进行中或已结束，不可新增场次" placement="top">
								<span>
									<el-button type="primary" size="small" class="btn-add-action" :disabled="!canAddExamSession(currentExam)" @click="handleAddExamSession">
										+ 新增科目场次
									</el-button>
								</span>
							</el-tooltip>
						</div>
					</div>

					<el-table :data="examSessions" v-loading="sessionListLoading" class="modern-table shadow-inner-table" style="width: 100%">
						<el-table-column type="index" label="序号" width="60" align="center">
							<template slot-scope="scope">
								<span class="index-badge">{{ scope.$index + 1 }}</span>
							</template>
						</el-table-column>
						<el-table-column prop="SubjectName" label="科目名称" min-width="120">
							<template slot-scope="scope">
								<span class="subject-bold-text">{{ scope.row.SubjectName }}</span>
							</template>
						</el-table-column>
						<el-table-column label="场次时间" width="230">
							<template slot-scope="scope">
								<div class="time-range-display aligned-left">
									<span class="time-start">{{ formatDate(scope.row.StartTime) }}</span>
									<span class="time-sep">至</span>
									<span class="time-end">{{ formatDate(scope.row.EndTime) }}</span>
								</div>
							</template>
						</el-table-column>
						<el-table-column label="状态" width="90" align="center">
							<template slot-scope="scope">
								<el-tag size="mini" :type="getExamStatusType(scope.row)">{{ getExamStatusText(scope.row) }}</el-tag>
							</template>
						</el-table-column>
						<el-table-column prop="Remark" label="备注说明" min-width="140" show-overflow-tooltip></el-table-column>
						<el-table-column fixed="right" label="操作" width="200" align="center">
							<template slot-scope="scope">
								<div class="table-op-pair">
									<el-tooltip :disabled="canEditExamSession(scope.row, currentExam)" content="场次或考试进行中/已结束，不可编辑" placement="top">
										<span>
											<el-button type="text" class="table-op-link" :disabled="!canEditExamSession(scope.row, currentExam)" @click="handleEditExamSession(scope.row)">编辑</el-button>
										</span>
									</el-tooltip>
									<el-tooltip :disabled="canDeleteExamSession(scope.row, currentExam)" content="场次或考试进行中/已结束，不可删除" placement="top">
										<span>
											<el-button type="text" class="table-op-link table-op-link--danger" :disabled="!canDeleteExamSession(scope.row, currentExam)" @click="handleDeleteExamSession(scope.row.Id)">删除</el-button>
										</span>
									</el-tooltip>
									<el-button type="text" class="table-op-link" @click="showSessionSignIn(scope.row)">签到记录</el-button>
								</div>
							</template>
						</el-table-column>
					</el-table>

					<div class="pagination-wrapper" style="margin-top: 10px;">
						<el-pagination background layout="total, prev, pager, next" @current-change="handleSessionCurrentChange"
							:page-size="sessionPageSize" :total="sessionTotal"></el-pagination>
					</div>
				</div>

				<!-- 2.2 场次签到记录 Dialog -->
				<el-dialog :title="signInDialogTitle" :visible.sync="signInDialogVisible"
					custom-class="glass-modal width-large" width="75%" :close-on-click-modal="false" :modal-append-to-body="false" :append-to-body="true">
					<el-alert
						v-if="currentSignInSession && !canManageSessionSignIn(currentSignInSession)"
						type="info"
						:closable="false"
						show-icon
						class="exam-lifecycle-alert"
						:title="getSignInBlockedMessage(currentSignInSession)" />
					<div class="signin-stats-row" v-if="signInStats">
						<span
							class="signin-stat-chip signin-stat-chip--total"
							:class="{ 'is-active': signInStatusFilter === 'all' }"
							@click="setSignInStatusFilter('all')">应到 {{ signInStats.Total }} 人</span>
						<span
							class="signin-stat-chip signin-stat-chip--signed"
							:class="{ 'is-active': signInStatusFilter === 'signed' }"
							@click="setSignInStatusFilter('signed')">已签 {{ signInStats.Signed }} 人</span>
						<span
							class="signin-stat-chip signin-stat-chip--unsigned"
							:class="{ 'is-active': signInStatusFilter === 'unsigned' }"
							@click="setSignInStatusFilter('unsigned')">未签 {{ signInStats.Unsigned }} 人</span>
						<span
							class="signin-stat-chip signin-stat-chip--manual"
							:class="{ 'is-active': signInStatusFilter === 'manual' }"
							@click="setSignInStatusFilter('manual')">补录 {{ signInStats.ManualCount }} 人</span>
						<el-button type="primary" size="mini" style="margin-left:auto" :loading="signInExportLoading" @click="exportSignInExcel">导出 Excel</el-button>
					</div>
					<div class="signin-filter-row">
						<span class="signin-filter-label">筛选考场</span>
						<el-select v-model="signInFilterRoomId" placeholder="全部考场" clearable style="width:200px" @change="handleSignInRoomFilterChange">
							<el-option v-for="room in signInRoomOptions" :key="room.ExamRoomId" :label="room.RoomName" :value="room.ExamRoomId" />
						</el-select>
						<span class="signin-filter-label">姓名搜索</span>
						<el-input
							v-model="signInNameKeyword"
							placeholder="输入姓名搜索"
							clearable
							prefix-icon="el-icon-search"
							style="width:200px"
							@input="handleSignInNameFilterChange"
							@clear="handleSignInNameFilterChange" />
					</div>
					<el-table :data="pagedSignInList" v-loading="signInListLoading" class="modern-table" max-height="420" style="width:100%">
						<el-table-column prop="StuName" label="姓名" width="100" />
						<el-table-column prop="StuCode" label="学号" width="120" />
						<el-table-column prop="ClassName" label="班级" min-width="120" />
						<el-table-column prop="ExamRoomName" label="考场" width="110" />
						<el-table-column prop="SeatNumber" label="座位号" width="80" align="center" />
						<el-table-column label="状态" width="90" align="center">
							<template slot-scope="scope">
								<el-tag v-if="scope.row.IsRevoked" type="info" size="mini">已撤销</el-tag>
								<el-tag v-else-if="scope.row.IsSigned" type="success" size="mini">已签</el-tag>
								<el-tag v-else type="warning" size="mini">未签</el-tag>
							</template>
						</el-table-column>
						<el-table-column prop="SignTime" label="签到时间" width="160">
							<template slot-scope="scope">{{ scope.row.SignTime ? formatDate(scope.row.SignTime) : '-' }}</template>
						</el-table-column>
						<el-table-column label="来源" width="90" align="center">
							<template slot-scope="scope">{{ formatSignSource(scope.row) }}</template>
						</el-table-column>
						<el-table-column fixed="right" label="操作" width="120" align="center">
							<template slot-scope="scope">
								<el-tooltip
									:disabled="canManageSessionSignIn(currentSignInSession)"
									:content="getSignInBlockedMessage(currentSignInSession)"
									placement="top">
									<span>
										<el-button
											v-if="!scope.row.IsRevoked && scope.row.IsSigned"
											type="text"
											size="small"
											class="btn-delete"
											:disabled="!canManageSessionSignIn(currentSignInSession)"
											@click="handleRevokeSignIn(scope.row)">撤销</el-button>
										<el-button
											v-if="!scope.row.IsRevoked && !scope.row.IsSigned"
											type="text"
											size="small"
											class="btn-view"
											:disabled="!canManageSessionSignIn(currentSignInSession)"
											@click="handleManualSignIn(scope.row)">补录</el-button>
									</span>
								</el-tooltip>
							</template>
						</el-table-column>
					</el-table>
					<div class="signin-pagination-row">
						<el-pagination
							small
							layout="total, prev, pager, next"
							:total="filteredSignInList.length"
							:current-page.sync="signInPage"
							:page-size="signInPageSize"
							@current-change="handleSignInPageChange" />
					</div>
				</el-dialog>
			</el-dialog>

			<!-- 2.1 新增/编辑考试场次 Dialog -->
			<el-dialog :visible.sync="sessionFormVisible"
				custom-class="glass-modal session-form-dialog" width="520px" :close-on-click-modal="false" @close="handleSessionFormClose"
				:append-to-body="true">
				<div slot="title" class="session-form-dialog-header">
					<div class="session-form-dialog-header__title">{{ sessionFormTitle }}</div>
					<div v-if="examTimeRangeText" class="session-form-dialog-header__range">
						<i class="el-icon-time"></i>
						<span>可选范围 {{ examTimeRangeText }}</span>
					</div>
				</div>
				<el-form :model="sessionFormData" label-width="88px" :rules="sessionFormRulesWithTime" ref="sessionFormRef" class="modern-form session-form-body">
					<el-alert
						v-if="!isSessionFormEditable"
						type="warning"
						:closable="false"
						show-icon
						class="exam-lifecycle-alert"
						title="当前场次或所属考试已开始/已结束，不可修改场次信息。" />
					<el-form-item label="考试科目" prop="SubjectId">
						<el-select v-model="sessionFormData.SubjectId" placeholder="请选择科目" style="width:100%" :disabled="!isSessionFormEditable">
							<el-option v-for="course in courseList" :key="course.Id" :label="course.CourseName"
								:value="course.Id"></el-option>
						</el-select>
					</el-form-item>
					<el-form-item label="起止时间" prop="timeRange">
						<el-date-picker
							:key="sessionTimePickerKey"
							v-model="sessionFormData.timeRange"
							type="datetimerange"
							start-placeholder="开始时间"
							end-placeholder="结束时间"
							format="yyyy-MM-dd HH:mm:ss"
							value-format="yyyy-MM-dd HH:mm:ss"
							:default-time="sessionDefaultTime"
							:picker-options="sessionTimePickerOptions"
							:disabled="!isSessionFormEditable"
							popper-append-to-body
							@change="handleSessionTimeChange"
							style="width: 100%" />
					</el-form-item>
					<el-form-item label="备注" class="session-form-remark-item">
						<el-input v-model="sessionFormData.Remark" type="textarea" :rows="2" placeholder="选填，场次相关说明" :disabled="!isSessionFormEditable"></el-input>
					</el-form-item>
				</el-form>
				<div slot="footer" class="dialog-footer">
					<el-button @click.native="sessionFormVisible = false">取消</el-button>
					<el-button v-if="isSessionFormEditable" type="primary" @click.native="submitSessionForm" :loading="sessionSubmitLoading" class="btn-submit">
						{{ isSessionEditMode ? '更新场次' : '提交场次' }}
					</el-button>
				</div>
			</el-dialog>

			<!-- 3. 考场编排 Dialog (左右工作空间对比) -->
			<el-dialog :visible.sync="roomAssignmentDialogVisible"
				custom-class="glass-modal width-large" width="85%" :close-on-click-modal="false" :modal-append-to-body="false" :append-to-body="true">
				<div slot="title" class="custom-dialog-header" v-if="currentExamSession">
					<span class="main-title">考场与考生编排 - {{ currentExamSession.ExamName }}</span>
					<div class="header-sub-info">
						<i class="el-icon-info icon-hint"></i>
						<span class="hint-text">当前考试下的所有科目场次将统一共用此处的考场编排与考生安排。</span>
					</div>
				</div>
				
				<div class="workspace-shuttle-container">
					<!-- 左侧：已分配考场 -->
					<div class="workspace-section card-left-assigned">
						<div class="section-title-badge color-blue">
							已分配考场 ({{ examSessionRooms.length }})
						</div>
						<div class="section-table-box">
							<el-table :data="examSessionRooms" v-loading="roomListLoading" class="modern-table no-border" max-height="420" style="width: 100%">
								<el-table-column prop="RoomName" label="考场名称" min-width="110">
									<template slot-scope="scope">
										<span class="room-assigned-name">{{ scope.row.RoomName }}</span>
									</template>
								</el-table-column>
								<el-table-column prop="Capacity" label="容量" width="70" align="center">
									<template slot-scope="scope">{{ scope.row.Capacity }}人</template>
								</el-table-column>
								<el-table-column prop="AssignedCount" label="已安排" width="75" align="center">
									<template slot-scope="scope">
										<el-tag size="mini" type="success">{{ scope.row.AssignedCount }}人</el-tag>
									</template>
								</el-table-column>
								<el-table-column prop="Building" label="所在位置" min-width="100" show-overflow-tooltip></el-table-column>
								<el-table-column fixed="right" label="操作与安排" width="180" align="center">
									<template slot-scope="scope">
										<div class="btn-group-row">
											<el-button type="text" size="small" class="btn-view" @click="showRoomInvigilatorManagement(scope.row)">分配监考</el-button>
											<span class="divider">|</span>
											<el-button type="text" size="small" class="btn-seat" @click="showStudentAssignment(scope.row)">考生安排</el-button>
										</div>
										<div class="btn-group-row" style="margin-top: 4px;">
											<el-button type="text" size="small" class="btn-delete-action" @click="removeRoom(scope.row.ExamRoomId)">移除考场</el-button>
											<span class="divider">|</span>
											<el-button type="text" size="small" class="btn-view" @click="openPrintSeatTable(scope.row)">打印座位表</el-button>
										</div>
									</template>
								</el-table-column>
							</el-table>
						</div>
					</div>

					<!-- 中间操作转移按钮 -->
					<div class="workspace-action-middle">
						<button class="shuttle-action-button" @click="assignRooms" :disabled="roomListLoading || availableRoomListLoading" title="分配选中考场到左侧">
							<span class="shuttle-btn-text">分配</span>
							<svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" stroke-width="2.5" fill="none">
								<polyline points="15 18 9 12 15 6"></polyline>
							</svg>
						</button>
					</div>

					<!-- 右侧：可选的可用考场 -->
					<div class="workspace-section card-right-available">
						<div class="section-title-badge color-green">
							可选可用考场 ({{ availableExamRooms.length }})
						</div>
						<div class="section-table-box">
							<el-table :data="availableExamRooms" v-loading="availableRoomListLoading" class="modern-table no-border" max-height="420"
								@selection-change="handleRoomSelectionChange" style="width: 100%">
								<el-table-column type="selection" width="50" align="center"></el-table-column>
								<el-table-column prop="RoomName" label="考场名称" min-width="110"></el-table-column>
								<el-table-column prop="Capacity" label="容量" width="70" align="center">
									<template slot-scope="scope">{{ scope.row.Capacity }}人</template>
								</el-table-column>
								<el-table-column prop="Building" label="所在位置" min-width="120" show-overflow-tooltip></el-table-column>
							</el-table>
						</div>
					</div>
				</div>

				<div slot="footer" class="dialog-footer">
					<el-button @click.native="roomAssignmentDialogVisible = false" class="btn-cancel">关闭编排</el-button>
				</div>
			</el-dialog>

			<!-- 4. 监考老师分配 Dialog (左右穿梭) -->
			<el-dialog :visible.sync="invigilatorDialogVisible"
				custom-class="glass-modal width-large modal-invigilator" width="82%" :close-on-click-modal="false" :modal-append-to-body="false" :append-to-body="true">
				<div slot="title" class="custom-dialog-header" v-if="currentExamSession && currentExamRoom">
					<span class="main-title">监考老师指派 - {{ currentExamRoom.RoomName }}</span>
					<div class="header-sub-info">
						<i class="el-icon-info icon-hint"></i>
						<span class="hint-text">当前考试下的所有科目场次将统一共用此处指派的监考人员。</span>
					</div>
				</div>
				
				<div class="workspace-shuttle-container">
					<!-- 左侧：已分配监考老师 -->
					<div class="workspace-section card-left-assigned">
						<div class="section-title-badge color-blue">
							已分配监考老师 ({{ examInvigilators.length }})
						</div>
						<div class="invig-grid-box" v-loading="invigilatorListLoading">
							<div v-if="!examInvigilators.length && !invigilatorListLoading" class="invig-empty">
								<i class="el-icon-user" style="font-size:32px;color:#cbd5e1"></i>
								<p>暂无已分配老师</p>
							</div>
							<div class="invig-grid">
								<div v-for="item in examInvigilators" :key="item.Id" class="invig-tile">
									<button class="invig-remove-btn" @click="removeExamInvigilator(item.Id)" title="移除">
										<i class="el-icon-close"></i>
									</button>
									<div class="invig-tile-avatar">
										<oss-el-image v-if="item.Avatar" :src="item.Avatar" fit="cover" :preview-src-list="item.Avatar ? [item.Avatar] : []">
											<div slot="error" class="invig-tile-avatar-fb"><i class="el-icon-user"></i></div>
										</oss-el-image>
										<div v-else class="invig-tile-avatar-fb"><i class="el-icon-user"></i></div>
									</div>
									<div class="invig-tile-name">{{ item.TeacherName }}</div>
									<div class="invig-tile-dept" :title="item.DepartmentName">{{ item.DepartmentName || '-' }}</div>
									<div class="invig-tile-card" v-if="item.CardNumber">
										<i class="el-icon-bank-card"></i> {{ item.CardNumber }}
									</div>
								</div>
							</div>
						</div>
					</div>

					<!-- 中间按鈕 -->
					<div class="workspace-action-middle">
						<button class="shuttle-action-button" @click="assignInvigilators"
							:disabled="invigilatorListLoading || availableInvigilatorListLoading || !selectedInvigilators.length"
							title="指派选中教师到左侧">
							<span class="shuttle-btn-text">指派</span>
							<svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" stroke-width="2.5" fill="none">
								<polyline points="15 18 9 12 15 6"></polyline>
							</svg>
						</button>
					</div>

					<!-- 右侧：可选监考老师 -->
					<div class="workspace-section card-right-available">
						<div class="section-title-badge color-green">
							可用监考老师 ({{ availableInvigilators.length }})
							<span v-if="selectedInvigilators.length" class="invig-selected-hint">已选 {{ selectedInvigilators.length }} 人</span>
						</div>
						<!-- 搜索框 -->
						<div class="invig-search-box">
							<el-input
								v-model="invigilatorSearchKeyword"
								placeholder="搜索教师姓名..."
								size="small"
								clearable
								prefix-icon="el-icon-search">
							</el-input>
						</div>
						<div class="invig-grid-box" v-loading="availableInvigilatorListLoading">
							<div v-if="!filteredAvailableInvigilators.length && !availableInvigilatorListLoading" class="invig-empty">
								<i class="el-icon-search" style="font-size:32px;color:#cbd5e1"></i>
								<p>{{ invigilatorSearchKeyword ? '未找到匹配老师' : '暂无可用老师' }}</p>
							</div>
							<div class="invig-grid">
								<div v-for="item in filteredAvailableInvigilators" :key="item.Id"
									:class="['invig-tile', 'invig-tile-selectable', isInvigilatorSelected(item) ? 'is-selected' : '']"
									@click="toggleInvigilatorSelect(item)">
									<div class="invig-check-badge" v-if="isInvigilatorSelected(item)">
										<i class="el-icon-check"></i>
									</div>
									<div class="invig-tile-avatar">
										<oss-el-image v-if="item.HeadPhotoImage" :src="item.HeadPhotoImage" fit="cover">
											<div slot="error" class="invig-tile-avatar-fb"><i class="el-icon-user"></i></div>
										</oss-el-image>
										<div v-else class="invig-tile-avatar-fb"><i class="el-icon-user"></i></div>
									</div>
									<div class="invig-tile-name">{{ item.TeacherName }}</div>
									<div class="invig-tile-dept" :title="item.DepartmentName">{{ item.DepartmentName || '-' }}</div>
									<div class="invig-tile-card" v-if="item.CardNumber">
										<i class="el-icon-bank-card"></i> {{ item.CardNumber }}
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				<div slot="footer" class="dialog-footer">
					<el-button @click.native="invigilatorDialogVisible = false">关闭</el-button>
				</div>
			</el-dialog>

			<!-- 5. 考生编排安排 Dialog (支持分页的穿梭区域) -->
			<el-dialog :visible.sync="studentAssignmentDialogVisible"
				custom-class="glass-modal width-large student-assignment-dialog" width="88%" :close-on-click-modal="false" :modal-append-to-body="false" :append-to-body="true">
				<div slot="title" class="custom-dialog-header student-assign-dialog-title" v-if="currentExamSession && currentExamRoomForStudent">
					<span class="main-title">考生分配 · {{ currentExamRoomForStudent.RoomName }}</span>
				</div>
				
				<div class="student-assign-hint">
					<i class="el-icon-info"></i>
					<span>所有科目场次共用此考场的座位与桌贴数据；导入后可在左侧列表微调，或使用「座位图」批量调整。</span>
				</div>

				<div class="workspace-shuttle-container student-assign-shuttle">
					<!-- 左侧：已分配考生 -->
					<div class="workspace-section card-left-assigned student-assigned-panel">
						<div class="student-table-card">
							<div class="student-table-toolbar">
								<div class="section-title-badge color-blue">
									已分配考生 ({{ examStudentTotal }})
								</div>
								<div class="student-table-toolbar-actions">
									<el-button type="primary" size="mini" plain icon="el-icon-s-grid"
										:disabled="examStudentTotal === 0"
										@click="openSeatMapEditor">座位图</el-button>
									<el-button type="danger" size="mini" plain icon="el-icon-delete"
										:disabled="selectedExamStudents.length === 0"
										@click="removeSelectedExamStudents">移除</el-button>
									<el-button type="warning" size="mini" plain icon="el-icon-refresh-left"
										:disabled="examStudentTotal === 0"
										@click="clearAllExamStudents">清空</el-button>
								</div>
							</div>
							<div class="section-table-box student-assigned-table-box">
								<div class="stu-grid-table stu-grid-table--assigned" v-loading="studentListLoading">
									<div class="stu-grid-head">
										<div class="stu-grid-cell stu-grid-cell--check">
											<el-checkbox
												:value="isAllExamStudentsSelected"
												:indeterminate="isExamStudentsIndeterminate"
												@change="toggleAllExamStudents" />
										</div>
										<div class="stu-grid-cell stu-grid-cell--name">考生</div>
										<div class="stu-grid-cell stu-grid-cell--code">学号</div>
										<div class="stu-grid-cell stu-grid-cell--seat">座位号</div>
										<div class="stu-grid-cell stu-grid-cell--action">操作</div>
									</div>
									<div class="stu-grid-body" :style="{ height: studentAssignTableHeight + 'px' }">
										<div v-if="!examStudents.length" class="stu-grid-empty">暂无已分配考生</div>
										<div
											v-for="row in examStudents"
											:key="row.Id || row.StudentId"
											class="stu-grid-row stu-grid-row--clickable"
											:class="{ 'is-selected': isExamStudentRowSelected(row) }"
											@click="handleExamStudentRowClick(row)">
											<div class="stu-grid-cell stu-grid-cell--check" @click.stop>
												<el-checkbox
													:value="isExamStudentRowSelected(row)"
													@change="(checked) => toggleExamStudentRowSelect(row, checked)" />
											</div>
											<div class="stu-grid-cell stu-grid-cell--name">
												<div class="student-cell-compact">
													<div class="student-cell-avatar" :style="!row.HeadPhotoImage ? getStudentAvatarStyle(row.StuName) : {}">
														<oss-el-image v-if="row.HeadPhotoImage" :src="row.HeadPhotoImage" class="avatar-img" fit="cover" />
														<span v-else>{{ getAvatarText(row.StuName) }}</span>
													</div>
													<div class="student-cell-meta">
														<div class="student-cell-name" :title="row.StuName">{{ row.StuName }}</div>
														<div class="student-cell-sub" :title="row.DepartmentName">{{ row.DepartmentName || '-' }}</div>
													</div>
												</div>
											</div>
											<div class="stu-grid-cell stu-grid-cell--code" :title="row.StuCode">{{ row.StuCode || '-' }}</div>
											<div class="stu-grid-cell stu-grid-cell--seat" @click.stop>
												<el-input
													v-model.number="row.SeatNumber"
													size="mini"
													class="seat-inline-input"
													@change="handleSeatNumberChange(row, row.SeatNumber)" />
											</div>
											<div class="stu-grid-cell stu-grid-cell--action" @click.stop>
												<el-button type="text" icon="el-icon-close" class="btn-remove-icon" @click="removeExamStudent(row)" title="移除"></el-button>
											</div>
										</div>
									</div>
								</div>
								<div class="table-footer-pagination">
									<el-pagination small layout="total, prev, pager, next" :total="examStudentTotal"
										:current-page.sync="examStudentPage" :page-size="examStudentPageSize"
										@current-change="handleExamStudentCurrentChange" />
								</div>
							</div>
						</div>
					</div>

					<!-- 中间按钮 -->
					<div class="workspace-action-middle">
						<button class="shuttle-action-button" @click="assignStudents" :disabled="studentListLoading || availableStudentListLoading" title="导入选中考生到左侧">
							<span class="shuttle-btn-text">导入</span>
							<svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" stroke-width="2.5" fill="none">
								<polyline points="15 18 9 12 15 6"></polyline>
							</svg>
						</button>
					</div>

					<!-- 右侧：待分配考生 -->
					<div class="workspace-section card-right-available student-pending-panel">
						<div class="student-table-card">
							<div class="student-table-toolbar">
								<div class="student-table-toolbar-left">
									<div class="section-title-badge color-green">
										待分配考生 ({{ availableStudentTotal }})
									</div>
									<span v-if="selectedStudents.length" class="student-selected-hint">
										已选 {{ selectedStudents.length }}
									</span>
								</div>
								<div class="student-table-toolbar-actions">
									<el-button
										type="text"
										size="mini"
										class="student-select-all-btn"
										:loading="pendingSelectAllLoading"
										:disabled="availableStudentTotal === 0"
										@click="selectAllPendingStudentsAcrossPages">
										{{ isAllPendingStudentsAcrossPagesSelected ? '取消全选' : '全选全部' }}
									</el-button>
									<el-button
										v-if="selectedStudents.length"
										type="text"
										size="mini"
										class="student-clear-select-btn"
										@click="clearPendingStudentSelection">
										清空已选
									</el-button>
								</div>
							</div>
							<div class="section-table-box student-pending-table-box">
								<div class="stu-grid-table stu-grid-table--pending" v-loading="availableStudentListLoading">
									<div class="stu-grid-head">
										<div class="stu-grid-cell stu-grid-cell--check">
											<el-checkbox
												:value="isAllPendingStudentsOnPageSelected"
												:indeterminate="isPendingStudentsOnPageIndeterminate"
												@change="toggleAllPendingStudentsOnPage" />
										</div>
										<div class="stu-grid-cell stu-grid-cell--name">考生</div>
										<div class="stu-grid-cell stu-grid-cell--code">学号</div>
										<div class="stu-grid-cell stu-grid-cell--seat stu-grid-cell--ghost"></div>
										<div class="stu-grid-cell stu-grid-cell--action stu-grid-cell--ghost"></div>
									</div>
									<div class="stu-grid-body" :style="{ height: studentAssignTableHeight + 'px' }">
										<div v-if="!availableStudents.length" class="stu-grid-empty">暂无数据</div>
										<div
											v-for="row in availableStudents"
											:key="row.StudentId || row.Id"
											class="stu-grid-row stu-grid-row--clickable"
											:class="{ 'is-selected': isPendingStudentRowSelected(row) }"
											@click="handlePendingStudentRowClick(row)">
											<div class="stu-grid-cell stu-grid-cell--check" @click.stop>
												<el-checkbox
													:value="isPendingStudentRowSelected(row)"
													@change="(checked) => togglePendingStudentRowSelect(row, checked)" />
											</div>
											<div class="stu-grid-cell stu-grid-cell--name">
												<div class="student-cell-compact">
													<div class="student-cell-avatar" :style="!row.HeadPhotoImage ? getStudentAvatarStyle(row.StuName) : {}">
														<oss-el-image v-if="row.HeadPhotoImage" :src="row.HeadPhotoImage" class="avatar-img" fit="cover" />
														<span v-else>{{ getAvatarText(row.StuName) }}</span>
													</div>
													<div class="student-cell-meta">
														<div class="student-cell-name" :title="row.StuName">{{ row.StuName }}</div>
														<div class="student-cell-sub" :title="row.DepartmentName">{{ row.DepartmentName || '-' }}</div>
													</div>
												</div>
											</div>
											<div class="stu-grid-cell stu-grid-cell--code" :title="row.StuCode">{{ row.StuCode || '-' }}</div>
											<div class="stu-grid-cell stu-grid-cell--seat stu-grid-cell--ghost"></div>
											<div class="stu-grid-cell stu-grid-cell--action stu-grid-cell--ghost"></div>
										</div>
									</div>
								</div>
								<div class="table-footer-pagination">
									<el-pagination small layout="total, prev, pager, next"
										:total="availableStudentTotal" :current-page.sync="availableStudentPage"
										:page-size="availableStudentPageSize"
										@current-change="handleAvailableStudentCurrentChange" />
								</div>
							</div>
						</div>
					</div>
				</div>

				<div slot="footer" class="dialog-footer">
					<el-button @click.native="studentAssignmentDialogVisible = false">关闭</el-button>
				</div>
			</el-dialog>

			<!-- 5.5 考生导入：座位图预览编排 Dialog -->
			<el-dialog :title="seatPreviewDialogTitle" :visible.sync="importPreviewDialogVisible" 
				width="65%" :close-on-click-modal="false" :modal-append-to-body="false" :append-to-body="true" custom-class="glass-modal width-large seat-preview-dialog">
				<div class="seat-preview-ops-card">
					<div v-for="group in seatSortRuleGroups" :key="group.key" class="seat-rule-row">
						<span class="seat-rule-row-label">{{ group.label }}</span>
						<div class="seat-rule-pills" :class="'seat-rule-pills--' + group.key">
							<el-tooltip
								v-for="rule in group.rules"
								:key="rule.key"
								:content="rule.tooltip"
								placement="top"
								:open-delay="300">
								<span class="seat-rule-pill-wrap">
									<button
										type="button"
										class="seat-rule-pill"
										:class="{ 'is-active': activeSeatSortRule === rule.key }"
										@click="applySeatSortRule(rule.key)">
										{{ rule.label }}
									</button>
								</span>
							</el-tooltip>
						</div>
					</div>
					<div class="seat-preview-legend">
						<template v-if="seatPreviewMode === 'import'">
							<span class="legend-chip legend-chip--locked">灰·已占用</span>
							<span class="legend-chip legend-chip--new">蓝·待导入</span>
						</template>
						<template v-else>
							<span class="legend-chip legend-chip--new">蓝·已分配考生</span>
						</template>
						<span class="legend-text">可拖拽蓝色卡片调座</span>
						<span v-if="seatRuleTip" class="legend-rule">
							当前「{{ activeSeatSortRuleTitle }}」：{{ seatRuleTip }}
						</span>
					</div>
				</div>

				<div v-if="hasDuplicateSeats" class="seat-preview-warn">
					<i class="el-icon-warning"></i>
					<span>存在重复或未分配的座位号（红色标记），请调整后再确认。</span>
				</div>

				<div class="seat-map-podium">讲台</div>
				<div class="seat-map-scroll-area seat-map-preview-area">
					<div class="seat-layout-grid" :style="previewSeatGridStyle">
						<div
							v-for="cell in previewSeatGridCells"
							:key="'preview-seat-' + cell.seatNumber"
							:class="previewSeatCellClass(cell)"
							:draggable="cell.isNew && !!cell.student"
							@dragstart="onPreviewSeatDragStart(cell, $event)"
							@dragover.prevent="onPreviewSeatDragOver(cell, $event)"
							@drop.prevent="onPreviewSeatDrop(cell)"
							@dragend="seatDragSource = null">
							<div class="seat-cell-no">{{ cell.seatNumber }}</div>
							<template v-if="cell.student">
								<div class="seat-cell-name" :title="cell.student.StuName">{{ cell.student.StuName }}</div>
								<div class="seat-cell-class" :title="cell.student.DepartmentName">{{ cell.student.DepartmentName || '-' }}</div>
							</template>
							<div v-else class="seat-cell-empty">空位</div>
						</div>
					</div>
				</div>

				<div slot="footer" class="dialog-footer">
					<el-button @click="importPreviewDialogVisible = false" size="small" class="btn-cancel">取消</el-button>
					<el-button type="primary" @click="confirmAssignWithSeats" :loading="assignLoading" size="small" class="btn-submit">
						{{ seatPreviewConfirmText }}
					</el-button>
				</div>
			</el-dialog>

			<!-- 6. 统一展示全部考试人员范围 Dialog。最后更新：2026-09-08 -->
			<el-dialog :title="allDeptDialogTitle" :visible.sync="showAllDeptDialog" custom-class="kq-dept-view-dialog glass-modal" width="640px" :close-on-click-modal="false" append-to-body>
				<div class="kq-view-head">
					<div class="kq-view-head-item">
						<span class="label">考试：</span>
						<span class="val">{{ currentExamRow ? currentExamRow.ExamName : '-' }}</span>
					</div>
					<div class="kq-view-head-item">
						<span class="label">所属部门：</span>
						<span class="val">{{ currentExamRow ? currentExamRow.DepartmentName : '-' }}</span>
					</div>
					<div class="kq-view-head-item">
						<span class="label">总数：</span>
						<span class="val count-badge">{{ allDeptNames ? allDeptNames.length : 0 }} 个班级/部门</span>
					</div>
				</div>

				<div class="kq-view-toolbar">
					<el-input v-model="allDeptFilterKey" size="small" placeholder="搜索班级 / 部门名称..." prefix-icon="el-icon-search"
						clearable class="kq-view-search-input" />
					<el-button size="small" type="primary" plain icon="el-icon-document-copy" @click="copyAllExamDeptNames">
						复制全部名称
					</el-button>
				</div>

				<div class="kq-view-table-wrap">
					<el-table :data="filteredAllDeptNames" size="small" max-height="340" border stripe class="kq-view-table">
						<el-table-column type="index" label="序号" width="70" align="center" />
						<el-table-column prop="name" label="班级 / 部门名称" min-width="260">
							<template slot-scope="scope">
								<span class="dept-text">{{ scope.row.name }}</span>
							</template>
						</el-table-column>
						<el-table-column label="操作" width="100" align="center">
							<template slot-scope="scope">
								<el-button type="text" size="mini" icon="el-icon-document-copy" @click="copySingleExamDeptName(scope.row.name)">复制</el-button>
							</template>
						</el-table-column>
						<div slot="empty" class="kq-view-empty">暂无匹配的班级/部门</div>
					</el-table>
				</div>

				<div slot="footer" class="dialog-footer">
					<el-button @click="showAllDeptDialog = false">关闭</el-button>
				</div>
			</el-dialog>

			<!-- 7. 展示全部监考老师标签 Dialog -->
			<el-dialog :title="allInvigilatorsDialogTitle" :visible.sync="showAllInvigilatorsDialog" custom-class="glass-modal" width="480px" :close-on-click-modal="false">
				<div class="all-tags-layout">
					<el-tag v-for="(item, index) in allInvigilators" :key="index" type="info" size="small" class="dialog-tag-pill">
						{{ item }}
					</el-tag>
					<div v-if="allInvigilators.length === 0" class="empty-placeholder">暂无监考老师</div>
				</div>
			</el-dialog>

			<!-- 8. 可视化座位大屏 (CSS Grid 座位表) -->
			<el-dialog :title="examStudentDialogTitle" :visible.sync="examStudentDialogVisible" custom-class="glass-modal width-large" width="80%" :close-on-click-modal="false">
				<div class="seat-visual-header-card">
					<div class="room-selector-section">
						<span class="label">选择监看考场：</span>
						<el-select v-model="selectedRoomId" filterable placeholder="请选择考场" style="width: 260px" @change="handleRoomChange">
							<el-option v-for="room in arrangedRooms" :key="room.ExamRoomId" :label="room.ExamRoomName" :value="room.ExamRoomId" />
						</el-select>
					</div>
					
					<!-- 考场参数仪表 -->
					<div class="room-meta-dashboard" v-if="roomInfo.ExamRoomName">
						<div class="meta-item">
							<span class="meta-label">考试地点</span>
							<span class="meta-value">{{ roomInfo.Building || '-' }}</span>
						</div>
						<div class="meta-item">
							<span class="meta-label">安排容量</span>
							<span class="meta-value highlight">{{ roomInfo.Capacity || '-' }} 人</span>
						</div>
						<div class="meta-item">
							<span class="meta-label">已排学生</span>
							<span class="meta-value success">{{ arrangedStudents.length }} 人</span>
						</div>
					</div>
				</div>

				<!-- 可视化网格桌面排布 -->
				<div class="seat-map-scroll-area">
					<div v-if="arrangedStudents.length > 0" class="desk-grid-layout">
						<div v-for="stu in arrangedStudents" :key="stu.Id" class="desk-card">
							<div class="desk-header">
								<span class="desk-no">{{ stu.SeatNumber < 10 ? '0' + stu.SeatNumber : stu.SeatNumber }}</span>
								<span class="desk-label">座位</span>
							</div>
							<div class="desk-body">
								<div class="student-avatar-box" :style="!stu.HeadPhotoImage ? getStudentAvatarStyle(stu.StuName) : {}">
									<oss-el-image 
										v-if="stu.HeadPhotoImage" 
										:src="stu.HeadPhotoImage" 
										:preview-src-list="stu.HeadPhotoImage ? [stu.HeadPhotoImage] : []"
										class="avatar-img" 
										style="cursor: pointer;"
										fit="cover" />
									<span v-else-if="!stu.HeadPhotoImage && stu.StuName" style="font-size: 11px; font-weight: 700;">{{ getAvatarText(stu.StuName) }}</span>
									<svg v-else viewBox="0 0 24 24" width="22" height="22" stroke="currentColor" stroke-width="2" fill="none">
										<path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
										<circle cx="12" cy="7" r="4"></circle>
									</svg>
								</div>
								<div class="student-details">
									<div class="student-name">{{ stu.StuName }}</div>
									<div class="student-code" :title="stu.StuCode || stu.CardNumber">学号: {{ stu.StuCode || '-' }}</div>
									<div class="student-class" :title="stu.DepartmentName">{{ stu.DepartmentName || '-' }}</div>
								</div>
							</div>
						</div>
					</div>
					
					<!-- 暂无考生 -->
					<div v-else class="seat-empty-view">
						<div class="empty-icon-box">
							<svg viewBox="0 0 24 24" width="48" height="48" stroke="currentColor" stroke-width="1.5" fill="none">
								<circle cx="12" cy="12" r="10"></circle>
								<line x1="8" y1="12" x2="16" y2="12"></line>
							</svg>
						</div>
						<div class="empty-text">当前所选考场暂无考生编排数据</div>
					</div>
				</div>

				<div slot="footer" class="dialog-footer">
					<el-button @click="examStudentDialogVisible = false" class="btn-cancel">关闭监看</el-button>
				</div>
			</el-dialog>

			<!-- 9. 打印座位表 Dialog -->
			<el-dialog :title="printSeatDialogTitle" :visible.sync="printSeatDialogVisible"
				custom-class="glass-modal width-large" width="70%" :close-on-click-modal="false" :append-to-body="true">
				<div ref="printSeatArea" class="print-seat-preview">
					<h2 class="print-seat-title">{{ currentExamSession ? currentExamSession.ExamName : '' }} - 座位表</h2>
					<div class="print-seat-meta" v-if="printSeatRoom">
						<span>考场：{{ printSeatRoom.RoomName || printSeatRoom.ExamRoomName }}</span>
						<span v-if="printSeatRoom.Building"> | 地点：{{ printSeatRoom.Building }}</span>
						<span> | 布局：{{ printSeatRoom.RowCount || 1 }} 行 × {{ printSeatRoom.ColumnCount || 1 }} 列</span>
					</div>
					<div class="print-seat-grid" :style="printSeatGridStyle">
						<div v-for="cell in printSeatGridCells" :key="cell.seatNumber" class="print-seat-cell">
							<div class="print-seat-no">{{ cell.seatNumber }}</div>
							<template v-if="cell.student">
								<div class="print-seat-name">{{ cell.student.StuName }}</div>
								<div class="print-seat-code">{{ cell.student.StuCode || '-' }}</div>
							</template>
							<div v-else class="empty-seat">空</div>
						</div>
					</div>
				</div>
				<div slot="footer" class="dialog-footer">
					<el-button @click="printSeatDialogVisible = false">关闭</el-button>
					<el-button type="primary" @click="doPrintSeatTable">打印</el-button>
				</div>
			</el-dialog>

			<!-- 考生信息向导导入弹窗 -->
			<input type="file" ref="upload" accept=".xls,.xlsx" style="display: none" v-if="excelInput" @input="readExcel" />
			<el-dialog :visible.sync="excelImportDialogVisible" :close-on-click-modal="false" custom-class="glass-modal width-large" width="70%" title="考生信息向导导入">
				<!-- 步骤进度条 -->
				<div class="import-steps-indicator">
					<el-steps :active="importStep" finish-status="success" align-center>
						<el-step title="下载模板与选择文件"></el-step>
						<el-step title="待导入数据预审校对"></el-step>
						<el-step title="导入结果与明细报告"></el-step>
					</el-steps>
				</div>

				<!-- Step 1: 下载模板与选择文件 -->
				<div v-if="importStep === 1" class="import-step-one">
					<div class="template-download-card" @click="downloadTemplateFile">
						<div class="card-icon">
							<svg viewBox="0 0 24 24" width="36" height="36" stroke="currentColor" stroke-width="2" fill="none">
								<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
								<polyline points="14 2 14 8 20 8"></polyline>
								<line x1="12" y1="18" x2="12" y2="12"></line>
								<polyline points="9 15 12 18 15 15"></polyline>
							</svg>
						</div>
						<div class="card-text">
							<h4>官方考生导入模板.xlsx</h4>
							<p>下载官方预设格式的 Excel 模板，填报完后上传</p>
							<p class="import-template-tip">学生ID为选填；不填时按「班级名称+学生姓名」匹配，同班同名请填写学生ID</p>
						</div>
						<el-button type="text" class="btn-download-text">一键下载</el-button>
					</div>

					<div class="file-upload-drag-area" @click="triggerFileInput">
						<div class="upload-icon">
							<svg viewBox="0 0 24 24" width="48" height="48" stroke="currentColor" stroke-width="1.5" fill="none">
								<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
								<polyline points="17 8 12 3 7 8"></polyline>
								<line x1="12" y1="3" x2="12" y2="15"></line>
							</svg>
						</div>
						<div class="upload-text">
							<h4>选择您已填报好的 Excel 考生名单</h4>
							<p>支持拖拽 XLSX 文件至此，或 <span class="highlight">点击这里上传</span></p>
						</div>
					</div>
				</div>

				<!-- Step 2: 待导入数据预审校对 -->
				<div v-if="importStep === 2" class="import-step-two">
					<div class="preview-stats-row">
						<span class="stats-label">预计导入数据共 <strong>{{ outputs.length }}</strong> 行，请核对：</span>
					</div>
					<el-table :data="outputs" ref="outputs_table" class="modern-table no-border" max-height="380" style="width: 100%">
						<el-table-column prop="ExamName" label="考试名称" min-width="140" show-overflow-tooltip></el-table-column>
						<el-table-column prop="ExamRoomName" label="考场名称" width="100"></el-table-column>
						<el-table-column prop="ClassName" label="班级名称" min-width="100"></el-table-column>
						<el-table-column prop="StudentName" label="学生姓名" width="90"></el-table-column>
						<el-table-column prop="SeatNumber" label="座位号" width="110" align="center">
							<template slot-scope="scope">
								<!-- 允许在预审阶段手动修改座位号，然后提交导入保存 -->
								<el-input-number v-model="scope.row.SeatNumber" size="mini" :min="1" :step="1" controls-position="right" style="width: 80px" />
							</template>
						</el-table-column>
						<el-table-column fixed="right" label="操作" align="center" width="70">
							<template slot-scope="scope">
								<span class="btn-remove-import-row" @click="removeImport(scope)">删除</span>
							</template>
						</el-table-column>
					</el-table>
				</div>

				<!-- Step 3: 导入结果与失败明细报告 -->
				<div v-if="importStep === 3" class="import-step-three">
					<!-- 可视化结果统计仪表 -->
					<div class="result-indicator-box" :class="{'all-success': importResult.FailCount === 0, 'part-fail': importResult.FailCount > 0}">
						<div class="result-radial-summary">
							<div class="summary-metric">
								<span class="metric-val">{{ importResult.SuccessCount }}</span>
								<span class="metric-label">成功导入</span>
							</div>
							<div class="summary-metric warning">
								<span class="metric-val">{{ importResult.FailCount }}</span>
								<span class="metric-label">失败行数</span>
							</div>
						</div>
						
						<div class="result-message-alert">
							<h4>{{ importResult.FailCount === 0 ? '🎉 考生信息全部导入成功！' : '⚠ 导入完成，但存在部分失败数据' }}</h4>
							<p>成功导入 {{ importResult.SuccessCount }} 名考生，有 {{ importResult.FailCount }} 条数据因冲突或不匹配未导入。</p>
						</div>
					</div>

					<!-- 错误行明细 -->
					<div v-if="importResult.FailCount > 0" class="error-details-section">
						<h4>详细失败原因清单 ({{ importResult.Errors ? importResult.Errors.length : 0 }} 项)</h4>
						<div class="error-logs-list">
							<div v-for="(err, idx) in (importResult.Errors || [])" :key="idx" class="error-log-item">
								<span class="item-index">{{ idx + 1 }}</span>
								<span class="item-text">{{ err }}</span>
							</div>
						</div>
					</div>
				</div>

				<!-- 底部控制按钮 -->
				<div slot="footer" class="dialog-footer">
					<el-button v-if="importStep === 2" @click="importStep = 1">返回重新上传</el-button>
					<el-button v-if="importStep === 2" type="primary" @click="SavaImport" class="btn-submit">开始导入</el-button>
					<el-button v-if="importStep === 3" type="primary" @click="excelImportDialogVisible = false">完成</el-button>
				</div>
			</el-dialog>

			<el-dialog
				title="强制删除考试"
				:visible.sync="forceDeleteDialogVisible"
				width="480px"
				append-to-body
				:modal-append-to-body="true"
				:lock-scroll="true"
				:close-on-click-modal="false"
				@close="resetForceDeleteDialog">
				<el-alert
					type="warning"
					:closable="false"
					show-icon
					title="超级管理员操作"
					description="将级联删除该考试下的场次、考场编排、监考安排、考生安排及签到记录，操作不可撤销。"
					style="margin-bottom: 16px"
				/>
				<div v-if="forceDeleteTarget" class="force-delete-target">
					<span class="force-delete-label">考试名称：</span>
					<span>{{ forceDeleteTarget.ExamName }}</span>
				</div>
				<el-form label-width="100px" size="small" style="margin-top: 16px" autocomplete="off">
					<el-form-item label="超密授权码" required>
						<el-input
							ref="forceDeleteAuthInput"
							v-model="forceDeleteAuthCode"
							clearable
							placeholder="请输入超密授权码"
							autocomplete="off"
							autocorrect="off"
							spellcheck="false"
							name="exam-force-delete-auth-code"
							readonly
							:class="{ 'custom-mask-input': !showForceDeleteAuthCode }"
							class="force-delete-auth-input"
							@focus="handleForceDeleteAuthFocus"
							@keyup.enter.native="confirmForceDelete">
							<i
								slot="suffix"
								:class="showForceDeleteAuthCode ? 'el-icon-view' : 'el-icon-minus'"
								class="force-delete-eye-icon"
								@click="showForceDeleteAuthCode = !showForceDeleteAuthCode" />
						</el-input>
					</el-form-item>
				</el-form>
				<div slot="footer">
					<el-button @click="forceDeleteDialogVisible = false">取消</el-button>
					<el-button type="danger" :loading="forceDeleteLoading" @click="confirmForceDelete">确认强制删除</el-button>
				</div>
			</el-dialog>
		</section>
	</section>
</template>

<script>
import util from '../../../util/date'
import { getButtonList } from '../../promissionRouter'
import Toolbar from '../../components/Toolbar'
import DepartmentPane from '../../components/DepartmentPane'
import { read, utils } from 'xlsx'

import { getExamList, addExam, updateExam, deleteExam, forceDeleteExam, getExamSessionList, addExamSession, updateExamSession, deleteExamSession, getExamSessionRooms, getAvailableExamRooms, assignExamSessionRooms, removeExamSessionRoom, getExamInvigilatorList, getAvailableInvigilators, assignInvigilator, removeInvigilator, getExamStudentList, getAvailableStudents, assignExamStudents, removeExamStudent, updateExamStudent, importExamStudentList, getArrangedStudentsByExamId, downloadStudentTemplate, getExamSignInList, manualExamSignIn, revokeExamSignIn, exportExamSignIn } from '../../api/modules/exam';
import { getSubDepartmentTree } from '../../api/modules/department';
import { GetSysCourseByDepartmentId } from '../../api/modules/schedule-course';
import { BaseApiUrl } from '../../api/base';
export default {
	components: { Toolbar, DepartmentPane },
	data() {
		return {
			props: { checkStrictly: true },
			searchList: [
				{
					label: '名称',
					key: 'name',
				},
			],
			buttonList: [],
			currentRow: null,
			options: [],
			filters: {},
			exams: [],
			total: 0,
			page: 1,
			pageSize: 10,
			listLoading: false,
			forceDeleteDialogVisible: false,
			forceDeleteAuthCode: '',
			showForceDeleteAuthCode: false,
			forceDeleteLoading: false,
			forceDeleteTarget: null,
			dialogVisible: false,
			isEditMode: false,
			dialogTitle: '',
			submitLoading: false,
			examTimePickerKey: 0,
			sessionTimePickerKey: 0,
			formData: {
				Id: 0,
				ExamName: '',
				DepartmentId: 0,
				ExamType: '',
				StartTime: '',
				EndTime: '',
				timeRange: [],
				Remark: '',
				DeptIds: [],
				ClassIds: ""
			},
			formRules: {
				ExamName: [{ required: true, message: '请输入考试名称', trigger: 'blur' }],
				DeptIds: [
					{ required: true, message: '请选择考试人员', trigger: 'change' },
				],
				timeRange: [
					{ required: true, message: '请选择考试起止时间', trigger: 'change' },
				],
			},
			DepartmentId: 0,
			multipleSelection: [],
			currentDepartmentName: '',

			// 考试场次相关数据
			examSessionDialogVisible: false,
			examSessionDialogTitle: '考试场次管理',
			examSessions: [],
			sessionTotal: 0,
			sessionPage: 1,
			sessionPageSize: 10,
			sessionListLoading: false,
			currentExam: {},
			editingExamRow: null,
			editingSessionRow: null,
			subjectName: '',

			// 科目列表
			courseList: [],
			courseLoading: false,

			// 考试场次表单数据
			sessionFormVisible: false,
			sessionFormTitle: '新增考试场次',
			isSessionEditMode: false,
			sessionSubmitLoading: false,
			sessionFormData: {
				Id: 0,
				ExamId: 0,
				SubjectId: '',
				SubjectName: '',
				StartTime: '',
				EndTime: '',
				timeRange: [],
				Remark: ''
			},
			sessionFormRules: {
				SubjectId: [
					{ required: true, message: '请选择科目', trigger: 'change' },
				],
			},

			// 考场编排相关数据
			roomAssignmentDialogVisible: false,
			roomAssignmentDialogTitle: '考场编排',
			examSessionRooms: [],
			availableExamRooms: [],
			selectedRooms: [],
			currentExamSession: {},
			roomListLoading: false,
			availableRoomListLoading: false,

			// 班级选择相关数据
			allOptions: [],
			isResouceShow: 0,

			// 考试人员弹窗相关。最后更新：2026-09-08
			showAllDeptDialog: false,
			allDeptNames: [],
			allDeptDialogTitle: '全部考试人员',
			currentExamRow: null,
			allDeptFilterKey: '',

			// 监考老师相关数据
			invigilatorDialogVisible: false,
			invigilatorDialogTitle: '监考老师管理',
			examInvigilators: [],
			availableInvigilators: [],
			selectedInvigilators: [],
			/** 右侧可用老师搜索关键字 */
			invigilatorSearchKeyword: '',
			currentExamRoom: {},
			invigilatorListLoading: false,
			availableInvigilatorListLoading: false,

			// 显示全部监考老师弹窗相关
			showAllInvigilatorsDialog: false,
			allInvigilators: [],
			allInvigilatorsDialogTitle: '全部监考老师',

			// 考生安排相关数据
			studentAssignmentDialogVisible: false,
			studentAssignmentDialogTitle: '考生安排',
			examStudents: [],
			availableStudents: [],
			selectedStudents: [],
			selectedExamStudents: [], // 当前勾选的已分配考生列表
			currentExamRoomForStudent: {},
			studentListLoading: false,
			availableStudentListLoading: false,
			availableStudentPage: 1,
			availableStudentPageSize: 10,
			availableStudentTotal: 0,
			pendingSelectAllLoading: false,
			importPreviewDialogVisible: false,
			/** 座位图模式：import 新导入 / edit 调整已分配 */
			seatPreviewMode: 'import',
			previewNewStudents: [],
			existingRoomSeatStudents: [],
			seatDragSource: null,
			assignLoading: false,
			/** 勾选导入时的原始顺序（studentId -> index） */
			previewSelectionOrder: {},
			/** 当前激活的排号规则 key */
			activeSeatSortRule: 'sequential',
			/** 当前激活规则标题 */
			activeSeatSortRuleTitle: '顺序号',
			/** 排号规则说明文案 */
			seatRuleTip: '按勾选顺序依次填入空位，座位从最小空号起逐行从左到右（Z形）编号。',
			/** 排号规则配置（悬停 tooltip + 点击说明统一来源） */
			seatSortRuleGroups: [
				{
					key: 'path',
					label: '走位方式',
					rules: [
						{
							key: 'sequential',
							label: '顺序号',
							tooltip: '按勾选顺序排人，座位从空位最小编号起，逐行从左到右填入（与 Z 形路径相同）。',
							tip: '按勾选顺序排人，座位从最小空位号起逐行从左到右（Z形路径）填入。'
						},
						{
							key: 'zShape',
							label: 'Z形',
							tooltip: '保持当前考生顺序不变，按 Z 形走位填座：每行从左到右，自上而下依次填入空位。',
							tip: '按 Z 形路径填座：每行从左到右，自上而下依次填入空位（考生顺序保持不变）。'
						},
						{
							key: 'snake',
							label: 'S形',
							tooltip: '保持当前考生顺序不变，按 S 形蛇形走位填座：奇数行左→右，偶数行右→左（多行考场时效果明显）。',
							tip: '按 S 形蛇形路径填座：奇数行从左到右，偶数行从右到左（多行考场时与 Z 形区别明显）。'
						}
					]
				},
				{
					key: 'sort',
					label: '考生排序',
					rules: [
						{
							key: 'stuCode',
							label: '学号↑',
							tooltip: '先将考生按学号从小到大排序，再按当前走位方式依次填入空位。',
							tip: '已按学号从小到大排列，再依次填入空位。'
						},
						{
							key: 'stuCodeDesc',
							label: '学号↓',
							tooltip: '先将考生按学号从大到小排序，再按当前走位方式依次填入空位。',
							tip: '已按学号从大到小排列，再依次填入空位。'
						},
						{
							key: 'name',
							label: '姓名',
							tooltip: '先将考生按姓名字母/拼音顺序排序，再按当前走位方式依次填入空位。',
							tip: '已按姓名字母/拼音顺序排列，再依次填入空位。'
						},
						{
							key: 'random',
							label: '随机',
							tooltip: '随机打乱考生顺序后，再按当前走位方式依次填入空位（防作弊常用）。',
							tip: '已随机打乱考生顺序，再依次填入空位。'
						},
						{
							key: 'classStuCode',
							label: '班级+学号',
							tooltip: '同班考生坐在一起，班内按学号升序排列，适合单班或按班集中就座。',
							tip: '已按班级分组、班内按学号排序，再依次填入空位。'
						},
						{
							key: 'classInterleave',
							label: '班级穿插',
							tooltip: '多班混考时使用：各班考生轮流占位，避免同班相邻，降低作弊风险。',
							tip: '已按班级轮流穿插排列（多班混考防作弊），再依次填入空位。'
						},
						{
							key: 'classSeat',
							label: '班内号次',
							tooltip: '按人员信息中的班内号次排序，沿用学生平时在教室的号次习惯。',
							tip: '已按班内号次顺序排列，再依次填入空位（需在人员信息中维护班内号次）。'
						},
						{
							key: 'genderAlternate',
							label: '男女穿插',
							tooltip: '男女考生交替排列后填座，需在人员信息中维护性别。',
							tip: '已按男女交替排列，再依次填入空位（需在人员信息中维护性别）。'
						}
					]
				}
			],
			/** 排号规则统一配置（悬停 tooltip + 点击说明） */
			seatSortRuleGroups: [
				{
					label: '走位方式',
					rules: [
						{
							key: 'sequential',
							label: '顺序号',
							tooltip: '按勾选顺序排人，座位从空位最小编号起，逐行从左到右填入（与 Z 形路径相同）。',
							tip: '已按勾选顺序排人，座位从最小空位号起逐行从左到右（Z形路径）填入。'
						},
						{
							key: 'zShape',
							label: 'Z形',
							tooltip: '保持当前考生顺序，按 Z 形走位填座：每行从左到右，自上而下。',
							tip: '已按 Z 形路径填座：每行从左到右，自上而下依次填入空位（考生顺序保持不变）。'
						},
						{
							key: 'snake',
							label: 'S形',
							tooltip: '保持当前考生顺序，按 S 形蛇形走位填座：第1行左→右，第2行右→左，交替进行（多行时效果明显）。',
							tip: '已按 S 形蛇形路径填座：奇数行从左到右，偶数行从右到左（多行考场时与 Z 形区别明显）。'
						}
					]
				},
				{
					label: '考生排序',
					rules: [
						{
							key: 'stuCode',
							label: '学号↑',
							tooltip: '按学号从小到大排列考生，再按 Z 形路径依次填入空位。',
							tip: '已按学号从小到大排列，再依次填入空位。'
						},
						{
							key: 'stuCodeDesc',
							label: '学号↓',
							tooltip: '按学号从大到小排列考生，再按 Z 形路径依次填入空位。',
							tip: '已按学号从大到小排列，再依次填入空位。'
						},
						{
							key: 'name',
							label: '姓名',
							tooltip: '按姓名字母/拼音顺序排列考生，再按 Z 形路径依次填入空位。',
							tip: '已按姓名字母/拼音顺序排列，再依次填入空位。'
						},
						{
							key: 'random',
							label: '随机',
							tooltip: '随机打乱考生顺序，再按 Z 形路径依次填入空位（防作弊常用）。',
							tip: '已随机打乱考生顺序，再依次填入空位。'
						},
						{
							key: 'classStuCode',
							label: '班级+学号',
							tooltip: '先按班级分组，班内再按学号排序，同班考生坐在一起（单班考场常用）。',
							tip: '已按班级分组、班内按学号排序，再依次填入空位。'
						},
						{
							key: 'classInterleave',
							label: '班级穿插',
							tooltip: '多个班级混考时，按班级轮流取人排列（如甲班1人、乙班1人…），降低作弊风险。',
							tip: '已按班级轮流穿插排列（多班混考防作弊），再依次填入空位。'
						},
						{
							key: 'classSeat',
							label: '班内号次',
							tooltip: '按人员信息中的班内号次排序，沿用学生平时在教室的号次习惯。',
							tip: '已按班内号次顺序排列，再依次填入空位（需在人员信息中维护号次）。'
						},
						{
							key: 'genderAlternate',
							label: '男女穿插',
							tooltip: '男女考生交替排列（男、女、男、女…），未登记性别的考生排在后面。',
							tip: '已按男女交替排列，再依次填入空位（需在人员信息中维护性别）。'
						}
					]
				}
			],

			// 已分配考生的分页属性
			examStudentPage: 1,
			examStudentPageSize: 10,
			examStudentTotal: 0,
			studentAssignTableHeight: 420,

			// Excel 相关
			outputs: [],
			excelInput: true,
			excelShow: false,
			excelImportDialogVisible: false,
			importStep: 1,
			importResult: {
				SuccessCount: 0,
				FailCount: 0,
				Errors: []
			},

			examStudentDialogVisible: false,
			examStudentDialogTitle: '',
			arrangedRooms: [],
			selectedRoomId: '',
			arrangedStudents: [],
			roomInfo: {},
			occupiedSeatNumbers: new Set(),

			// 场次签到记录
			signInDialogVisible: false,
			signInDialogTitle: '',
			signInListLoading: false,
			signInExportLoading: false,
			signInList: [],
			signInStats: null,
			signInFilterRoomId: '',
			signInRoomOptions: [],
			currentSignInSession: null,
			signInNameKeyword: '',
			signInStatusFilter: 'all', // all | signed | unsigned | manual
			signInPage: 1,
			signInPageSize: 10,

			// 打印座位表
			printSeatDialogVisible: false,
			printSeatRoom: null,
			printSeatStudents: []
		}
	},
	computed: {
		/** 弹窗中过滤后的考试班级/部门列表。最后更新：2026-09-08 */
		filteredAllDeptNames() {
			const list = this.allDeptNames || [];
			const key = (this.allDeptFilterKey || '').trim().toLowerCase();
			if (!key) {
				return list.map((name) => ({ name }));
			}
			return list
				.filter((name) => String(name).toLowerCase().includes(key))
				.map((name) => ({ name }));
		},
		/** 按姓名过滤可用监考老师列表 */
		filteredAvailableInvigilators() {
			const kw = (this.invigilatorSearchKeyword || '').trim()
			if (!kw) return this.availableInvigilators
			return this.availableInvigilators.filter(t =>
				(t.TeacherName || '').includes(kw)
			)
		},
		/** 签到记录：按状态/姓名筛选后的列表（不影响顶部统计数） */
		filteredSignInList() {
			let list = this.signInList || []
			const keyword = (this.signInNameKeyword || '').trim()
			if (keyword) {
				list = list.filter(item => (item.StuName || '').includes(keyword))
			}
			if (this.signInStatusFilter === 'signed') {
				list = list.filter(item => !!item.IsSigned && !item.IsRevoked)
			} else if (this.signInStatusFilter === 'unsigned') {
				list = list.filter(item => !item.IsSigned && !item.IsRevoked)
			} else if (this.signInStatusFilter === 'manual') {
				list = list.filter(item => !!item.IsManual && !!item.IsSigned && !item.IsRevoked)
			}
			return list
		},
		/** 签到记录：当前页数据 */
		pagedSignInList() {
			const start = (this.signInPage - 1) * this.signInPageSize
			return this.filteredSignInList.slice(start, start + this.signInPageSize)
		},
		/** 场次表单是否可编辑 */
		isSessionFormEditable() {
			if (this.isSessionEditMode) {
				return this.canEditExamSession(this.editingSessionRow, this.currentExam)
			}
			return this.canAddExamSession(this.currentExam)
		},
		/** 所属考试时间范围（仅日期部分，用于标题展示） */
		examTimeRangeText() {
			const exam = this.currentExam
			if (!exam || !exam.StartTime || !exam.EndTime) return ''
			return `${this.formatDate(exam.StartTime)} 至 ${this.formatDate(exam.EndTime)}`
		},
		/** 所属考试时间范围提示（用于校验报错） */
		examTimeRangeHint() {
			const rangeText = this.examTimeRangeText
			if (!rangeText) return ''
			return `场次时间须在考试时间范围内：${rangeText}`
		},
		/** 场次时间选择器：默认时刻（边界日与考试时间对齐） */
		sessionDefaultTime() {
			const exam = this.currentExam
			if (!exam || !exam.StartTime || !exam.EndTime) {
				return ['00:00:00', '23:59:59']
			}
			return [
				this.formatTimePart(exam.StartTime),
				this.formatTimePart(exam.EndTime)
			]
		},
		/** 场次时间选择器：仅允许选考试日期范围内的日期 */
		sessionTimePickerOptions() {
			const exam = this.currentExam
			if (!exam || !exam.StartTime || !exam.EndTime) {
				return {}
			}
			const examStart = new Date(exam.StartTime)
			const examEnd = new Date(exam.EndTime)
			const startDay = new Date(
				examStart.getFullYear(),
				examStart.getMonth(),
				examStart.getDate()
			).getTime()
			const endDay = new Date(
				examEnd.getFullYear(),
				examEnd.getMonth(),
				examEnd.getDate()
			).getTime()
			return {
				disabledDate: (time) => {
					const day = new Date(
						time.getFullYear(),
						time.getMonth(),
						time.getDate()
					).getTime()
					return day < startDay || day > endDay
				}
			}
		},
		/** 场次表单校验规则（含时间范围） */
		sessionFormRulesWithTime() {
			return {
				SubjectId: this.sessionFormRules.SubjectId,
				timeRange: [
					{ validator: this.validateSessionTimeRange, trigger: 'change' }
				]
			}
		},
		/** 座位图弹窗标题 */
		seatPreviewDialogTitle() {
			return this.seatPreviewMode === 'edit' ? '座位编排调整' : '座位编排确认'
		},
		/** 座位图弹窗确认按钮文案 */
		seatPreviewConfirmText() {
			return this.seatPreviewMode === 'edit' ? '保存座位调整' : '确认导入并排座'
		},
		/** 已分配考生是否全选 */
		isAllExamStudentsSelected() {
			return this.examStudents.length > 0 && this.selectedExamStudents.length === this.examStudents.length
		},
		/** 已分配考生是否半选 */
		isExamStudentsIndeterminate() {
			return this.selectedExamStudents.length > 0 && this.selectedExamStudents.length < this.examStudents.length
		},
		/** 待分配考生当前页是否全选 */
		isAllPendingStudentsOnPageSelected() {
			if (!this.availableStudents.length) return false
			return this.availableStudents.every(row => this.isPendingStudentRowSelected(row))
		},
		/** 待分配考生当前页是否半选 */
		isPendingStudentsOnPageIndeterminate() {
			const selectedOnPage = this.availableStudents.filter(row => this.isPendingStudentRowSelected(row)).length
			return selectedOnPage > 0 && selectedOnPage < this.availableStudents.length
		},
		/** 待分配考生是否已全选全部页 */
		isAllPendingStudentsAcrossPagesSelected() {
			return this.availableStudentTotal > 0 && this.selectedStudents.length >= this.availableStudentTotal
		},
		/** 获取重复的座位号集合 */
		duplicateSeatNumbers() {
			const counts = {}
			const duplicates = new Set()
			const lockedSeats = this.seatPreviewMode === 'import'
				? new Set(
					(this.existingRoomSeatStudents || [])
						.map(s => Number(s.SeatNumber))
						.filter(n => n > 0)
				)
				: new Set()

			;(this.previewNewStudents || []).forEach(s => {
				const seat = Number(s.SeatNumber)
				if (seat) {
					counts[seat] = (counts[seat] || 0) + 1
				}
			})

			;(this.previewNewStudents || []).forEach(s => {
				const seat = Number(s.SeatNumber)
				if (!seat) {
					duplicates.add(0)
					return
				}
				if (counts[seat] > 1 || lockedSeats.has(seat)) {
					duplicates.add(seat)
				}
			})

			return duplicates
		},
		/** 判断是否有重复的座位号 */
		hasDuplicateSeats() {
			if (this.duplicateSeatNumbers.has(0)) return true
			return this.duplicateSeatNumbers.size > 0
		},
		/** 座位图预览：考场行列布局 */
		previewSeatLayoutMeta() {
			const room = this.currentExamRoomForStudent || {}
			let rows = Number(room.RowCount) || 0
			let cols = Number(room.ColumnCount) || 0
			const capacity = Number(room.Capacity) || 0
			if (!rows && !cols && capacity > 0) {
				cols = Math.ceil(Math.sqrt(capacity))
				rows = Math.ceil(capacity / cols)
			}
			if (!rows) rows = 6
			if (!cols) cols = 6
			const total = capacity > 0 ? capacity : rows * cols
			return { rows, cols, capacity: total }
		},
		/** 座位图预览：网格单元格 */
		previewSeatGridCells() {
			const { capacity } = this.previewSeatLayoutMeta
			const seatMap = {}

			;(this.existingRoomSeatStudents || []).forEach(s => {
				const seat = Number(s.SeatNumber)
				if (seat > 0) {
					seatMap[seat] = { student: s, isLocked: true, isNew: false }
				}
			})
			;(this.previewNewStudents || []).forEach(s => {
				const seat = Number(s.SeatNumber)
				if (seat > 0) {
					seatMap[seat] = { student: s, isLocked: false, isNew: true }
				}
			})

			const cells = []
			for (let i = 1; i <= capacity; i++) {
				const item = seatMap[i]
				cells.push({
					seatNumber: i,
					student: item ? item.student : null,
					isLocked: item ? item.isLocked : false,
					isNew: item ? item.isNew : false
				})
			}
			return cells
		},
		/** 座位图预览：Grid 列样式 */
		previewSeatGridStyle() {
			const cols = this.previewSeatLayoutMeta.cols || 1
			return { gridTemplateColumns: `repeat(${cols}, minmax(72px, 1fr))` }
		},
		/** 打印座位表弹窗标题 */
		printSeatDialogTitle() {
			if (!this.printSeatRoom) return '打印座位表'
			return `打印座位表 - ${this.printSeatRoom.RoomName || this.printSeatRoom.ExamRoomName || ''}`
		},
		/** 按考场行列生成座位格 */
		printSeatGridCells() {
			const room = this.printSeatRoom
			if (!room) return []
			const rows = Number(room.RowCount) || 1
			const cols = Number(room.ColumnCount) || 1
			const total = rows * cols
			const seatMap = {}
			;(this.printSeatStudents || []).forEach(s => {
				const seat = Number(s.SeatNumber)
				if (seat) seatMap[seat] = s
			})
			const cells = []
			for (let i = 1; i <= total; i++) {
				cells.push({ seatNumber: i, student: seatMap[i] || null })
			}
			return cells
		},
		/** 打印座位表 Grid 样式 */
		printSeatGridStyle() {
			const room = this.printSeatRoom
			const cols = room ? (Number(room.ColumnCount) || 1) : 1
			return { gridTemplateColumns: `repeat(${cols}, 1fr)` }
		}
	},
	methods: {
		/** 获取教师头像完整 URL */
		getTeacherAvatar(path) {
			if (!path) return ''
			if (path.startsWith('http')) return path
			const base = BaseApiUrl || ''
			const hasBaseSlash = base.endsWith('/')
			const hasPathSlash = path.startsWith('/')
			if (!hasBaseSlash && !hasPathSlash) {
				return base + '/' + path
			} else if (hasBaseSlash && hasPathSlash) {
				return base + path.substring(1)
			}
			return base + path
		},
		// 解析部门/班级名称数组。最后更新：2026-09-08
		parseExamDeptNames(val) {
			if (!val) return [];
			if (Array.isArray(val)) return val;
			return String(val).split(/[、,，;；]/).map(s => s.trim()).filter(s => s.length > 0);
		},
		// 打开全部考试人员范围弹窗。最后更新：2026-09-08
		showAllDeptNames(rowOrDeptNames) {
			if (typeof rowOrDeptNames === 'object' && rowOrDeptNames !== null) {
				this.currentExamRow = rowOrDeptNames;
				this.allDeptNames = this.parseExamDeptNames(rowOrDeptNames.DeptNames);
				this.allDeptDialogTitle = `查看考试人员范围 - ${rowOrDeptNames.ExamName || '考试'}`;
			} else {
				this.currentExamRow = null;
				this.allDeptNames = this.parseExamDeptNames(rowOrDeptNames);
				this.allDeptDialogTitle = '查看考试人员范围';
			}
			this.allDeptFilterKey = '';
			this.showAllDeptDialog = true;
		},
		// 复制全部班级/部门
		copyAllExamDeptNames() {
			const depts = (this.filteredAllDeptNames || []).map((item) => item.name).filter(Boolean);
			if (!depts.length) {
				this.$message.warning('暂无可复制的内容');
				return;
			}
			this.copyTextToClipboard(depts.join('、'));
			this.$message.success(`已复制 ${depts.length} 个班级/部门名称`);
		},
		// 复制单个名称
		copySingleExamDeptName(text) {
			if (!text) return;
			this.copyTextToClipboard(text);
			this.$message.success(`已复制: ${text}`);
		},
		// 通用剪贴板复制工具
		copyTextToClipboard(text) {
			if (navigator.clipboard && window.isSecureContext) {
				navigator.clipboard.writeText(text).catch(() => {
					this.fallbackCopyText(text);
				});
			} else {
				this.fallbackCopyText(text);
			}
		},
		fallbackCopyText(text) {
			const textArea = document.createElement('textarea');
			textArea.value = text;
			textArea.style.position = 'fixed';
			textArea.style.left = '-999999px';
			textArea.style.top = '-999999px';
			document.body.appendChild(textArea);
			textArea.focus();
			textArea.select();
			try {
				document.execCommand('copy');
			} catch (err) {
				console.error('复制失败', err);
			}
			document.body.removeChild(textArea);
		},
		treeNodeClick(data, node, component) {
			this.DepartmentId = data.value
			this.currentDepartmentName = data.label || data.name || ''
			this.handleQuery()
		},
		callFunction(item) {
			if (typeof item.search == 'object') {
				this.filters = item.search
			} else {
				this.filters = {
					key: item.search,
				}
			}
			this[item.Func].apply(this, item)
		},
		formatDate(date) {
			return !date || date == ''
				? ''
				: util.formatDate.format(
					new Date(date),
					'yyyy-MM-dd hh:mm:ss'
				)
		},
		/** 规范化为接口使用的日期时间字符串 */
		formatDateTime(value) {
			if (!value) return ''
			if (typeof value === 'string') {
				const text = value.trim()
				if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(text)) {
					return text
				}
			}
			const date = value instanceof Date ? value : new Date(value)
			if (Number.isNaN(date.getTime())) return ''
			return util.formatDate.format(date, 'yyyy-MM-dd hh:mm:ss')
		},
		/** 构建日期范围选择器初始值 */
		buildTimeRange(startTime, endTime) {
			const startText = this.formatDateTime(startTime)
			const endText = this.formatDateTime(endTime)
			if (!startText || !endText) return []
			return [startText, endText]
		},
		/** 提取时间部分 HH:mm:ss */
		formatTimePart(value) {
			const text = this.formatDateTime(value)
			if (!text) return '00:00:00'
			return text.split(' ')[1] || '00:00:00'
		},
		/** 解析日期时间为时间戳 */
		parseDateTimeValue(value) {
			if (!value) return NaN
			if (value instanceof Date) return value.getTime()
			const text = String(value).trim().replace(/-/g, '/')
			return new Date(text).getTime()
		},
		/** 校验场次时间是否在考试时间范围内 */
		validateSessionTimeRange(rule, value, callback) {
			const range = value || this.sessionFormData.timeRange
			if (!range || range.length !== 2 || !range[0] || !range[1]) {
				callback(new Error('请选择场次起止时间'))
				return
			}
			const sessionStart = this.parseDateTimeValue(range[0])
			const sessionEnd = this.parseDateTimeValue(range[1])
			if (Number.isNaN(sessionStart) || Number.isNaN(sessionEnd)) {
				callback(new Error('场次时间格式无效'))
				return
			}
			if (sessionStart >= sessionEnd) {
				callback(new Error('场次开始时间必须早于结束时间'))
				return
			}
			const exam = this.currentExam
			if (!exam || !exam.StartTime || !exam.EndTime) {
				callback()
				return
			}
			const examStart = this.parseDateTimeValue(exam.StartTime)
			const examEnd = this.parseDateTimeValue(exam.EndTime)
			if (sessionStart < examStart || sessionEnd > examEnd) {
				callback(new Error(
					`场次时间须在考试时间范围内（${this.formatDate(exam.StartTime)} 至 ${this.formatDate(exam.EndTime)}）`
				))
				return
			}
			callback()
		},
		/** 获取考试/场次生命周期状态 */
		getLifecycleStatus(row) {
			if (!row || !row.StartTime || !row.EndTime) return 'unknown'
			const now = new Date()
			const start = new Date(row.StartTime)
			const end = new Date(row.EndTime)
			if (now < start) return 'pending'
			if (now > end) return 'ended'
			return 'ongoing'
		},
		/** 考试状态文案（纯前端） */
		getExamStatusText(row) {
			const status = this.getLifecycleStatus(row)
			if (status === 'pending') return '未开始'
			if (status === 'ongoing') return '进行中'
			if (status === 'ended') return '已结束'
			return '未知'
		},
		/** 考试状态 tag 类型 */
		getExamStatusType(row) {
			const status = this.getLifecycleStatus(row)
			if (status === 'pending') return 'info'
			if (status === 'ongoing') return 'success'
			if (status === 'ended') return ''
			return 'warning'
		},
		/** 是否可编辑考试基础信息（仅未开始） */
		canEditExamInfo(row) {
			return this.getLifecycleStatus(row) === 'pending'
		},
		/** 是否可删除考试（仅未开始） */
		canDeleteExam(row) {
			return this.canEditExamInfo(row)
		},
		/** 是否可编排考场/考生（仅未开始） */
		canManageExamArrangement(exam) {
			return this.canEditExamInfo(exam)
		},
		/** 是否可新增场次（所属考试未开始） */
		canAddExamSession(exam) {
			return this.canEditExamInfo(exam)
		},
		/** 是否可编辑场次（场次与所属考试均未开始） */
		canEditExamSession(session, exam) {
			if (!this.canEditExamInfo(exam)) return false
			return this.getLifecycleStatus(session) === 'pending'
		},
		/** 是否可删除场次 */
		canDeleteExamSession(session, exam) {
			return this.canEditExamSession(session, exam)
		},
		/** 是否可补录/撤销签到（仅场次进行中） */
		canManageSessionSignIn(session) {
			return this.getLifecycleStatus(session) === 'ongoing'
		},
		/** 签到操作不可用提示 */
		getSignInBlockedMessage(session) {
			const status = this.getLifecycleStatus(session)
			if (status === 'pending') return '场次未开始，仅可查看签到记录，不可补录或撤销。'
			if (status === 'ended') return '场次已结束，仅可查看签到记录，不可补录或撤销。'
			return '当前不可补录或撤销签到。'
		},
		/** 获取不可编辑提示 */
		getEditBlockedMessage(row, type) {
			const statusText = this.getExamStatusText(row)
			if (type === 'exam') {
				return `考试${statusText}，不可修改考试信息`
			}
			if (type === 'session') {
				return `场次${statusText}，不可修改场次信息`
			}
			return `当前${statusText}，不可操作`
		},
		/** 获取当前操作人姓名 */
		getOperatorName() {
			try {
				const user = JSON.parse(window.localStorage.user || '{}')
				return user.uRealName || '管理员'
			} catch (e) {
				return '管理员'
			}
		},
		handleCurrentChange(val) {
			this.page = val
			this.handleQuery()
		},
		// 获取考试列表
		handleQuery() {
			let para = {
				page: this.page,
				departmentId: this.DepartmentId,
				name: this.filters.name || '',
				intPageSize: this.pageSize
			}
			this.listLoading = true

			getExamList(para).then((res) => {
				if (res.data.success && res.data.response) {
					this.exams = res.data.response.data
					this.total = res.data.response.dataCount
					this.pageSize = res.data.response.PageSize
				}
				this.listLoading = false
			}).catch(error => {
				console.error('获取考试列表失败:', error)
				this.listLoading = false
			})
		},
		handleSelectionChange(val) {
			this.multipleSelection = val
		},
		// 显示编辑界面
		handleEdit(row) {
			if (!this.canEditExamInfo(row)) {
				this.$message.warning(this.getEditBlockedMessage(row, 'exam'))
				return
			}
			this.allOptions = [];
			console.log('编辑考试数据:', row)
			let allpara = { pid: row.DepartmentId };
			getSubDepartmentTree(allpara).then((res) => {
				++this.isResouceShow;
				this.allOptions.push(res.data.response);
			});

			this.editingExamRow = row
			const timeRange = this.buildTimeRange(row.StartTime, row.EndTime)
			this.formData = {
				Id: row.Id,
				ExamName: row.ExamName || '',
				DepartmentId: row.DepartmentId || 0,
				ExamType: row.ExamType || '',
				Remark: row.Remark || '',
				DeptIds: Array.isArray(row.DeptIds) ? JSON.parse(JSON.stringify(row.DeptIds)) : [],
				ClassIds: row.ClassIds || '',
				timeRange: timeRange,
				StartTime: timeRange[0] || '',
				EndTime: timeRange[1] || ''
			}
			this.dialogTitle = '编辑考试'
			this.isEditMode = true
			this.examTimePickerKey += 1
			this.dialogVisible = true
			this.$nextTick(() => {
				if (this.$refs.formRef) {
					this.$refs.formRef.clearValidate()
				}
			})
		},
		// 显示新增界面
		handleAdd() {
			this.editingExamRow = null
			this.formData = {
				Id: 0,
				ExamName: '',
				DepartmentId: this.DepartmentId || 0,
				ExamType: '',
				StartTime: '',
				EndTime: '',
				timeRange: [],
				Remark: '',
				DeptIds: [],
				ClassIds: ""
			}
			this.dialogTitle = '新增考试'
			this.isEditMode = false
			this.examTimePickerKey += 1
			this.dialogVisible = true
			this.allOptions = [];
			let allpara = { pid: this.DepartmentId };
			getSubDepartmentTree(allpara).then((res) => {
				++this.isResouceShow;
				this.allOptions.push(res.data.response);
			});
		},
		handleDialogClose() {
			this.dialogVisible = false
			this.editingExamRow = null
			if (this.$refs.formRef) {
				this.$refs.formRef.resetFields()
			}
		},
		// 提交考试数据
		submitForm() {
			if (this.isEditMode && !this.canEditExamInfo(this.editingExamRow || this.formData)) {
				this.$message.warning(this.getEditBlockedMessage(this.editingExamRow || this.formData, 'exam'))
				return
			}
			this.$refs.formRef.validate((valid) => {
				if (valid) {
					this.$confirm('确认提交吗？', '提示', { type: 'info' }).then(() => {
						this.syncExamTimeFromRange()
						this.submitLoading = true
						let para = Object.assign({}, this.formData)

						delete para.timeRange
						delete para.DeptIds
						delete para.DeptNames
						delete para.SessionCount
						delete para.RoomCount
						delete para.StudentCount
						delete para.AssignedCount
						delete para.UnassignedCount
						delete para.DepartmentName
						delete para.Dids

						if (!para.DepartmentId || para.DepartmentId === 0) {
							para.DepartmentId = this.DepartmentId || 0
						}

						let deptIdStr = ''
						;(this.formData.DeptIds || []).forEach(element => {
							if (Array.isArray(element) && element.length) {
								deptIdStr += element[element.length - 1] + ','
							}
						})
						para.ClassIds = deptIdStr

						const apiCall = this.isEditMode ? updateExam : addExam

						apiCall(para).then((res) => {
							this.submitLoading = false
							if (res.data.success) {
								this.$message({
									message: res.data.msg,
									type: 'success',
								})
								this.dialogVisible = false
								this.handleQuery()
							} else {
								this.$message({
									message: res.data.msg,
									type: 'error',
								})
							}
						}).catch(error => {
							console.error('提交考试信息失败:', error)
							this.submitLoading = false
						})
					})
				}
			})
		},
		openForceDeleteDialog(row) {
			this.forceDeleteTarget = row
			this.forceDeleteAuthCode = ''
			this.showForceDeleteAuthCode = false
			this.forceDeleteDialogVisible = true
			this.$nextTick(() => {
				const inputEl = this.$refs.forceDeleteAuthInput
				const nativeInput = inputEl && inputEl.$refs && inputEl.$refs.input
				if (nativeInput) {
					nativeInput.setAttribute('readonly', 'readonly')
					nativeInput.setAttribute('autocomplete', 'off')
				}
			})
		},
		handleForceDeleteAuthFocus(event) {
			const target = event && event.target
			if (target) {
				target.removeAttribute('readonly')
			}
		},
		resetForceDeleteDialog() {
			this.forceDeleteTarget = null
			this.forceDeleteAuthCode = ''
			this.showForceDeleteAuthCode = false
			this.forceDeleteLoading = false
		},
		confirmForceDelete() {
			if (!this.forceDeleteAuthCode) {
				this.$message.error('请输入超密授权码')
				return
			}
			if (!this.forceDeleteTarget || !this.forceDeleteTarget.Id) {
				this.$message.error('未选择考试')
				return
			}
			const examName = this.forceDeleteTarget.ExamName || '该考试'
			this.$confirm(`确认强制删除「${examName}」？将级联删除所有关联数据，此操作不可撤销。`, '二次确认', { type: 'warning' })
				.then(() => {
					this.forceDeleteLoading = true
					forceDeleteExam({
						Id: this.forceDeleteTarget.Id,
						AuthCode: this.forceDeleteAuthCode
					}).then((res) => {
						this.forceDeleteLoading = false
						if (res.data && res.data.success) {
							this.$message.success(res.data.msg || '强制删除成功')
							this.forceDeleteDialogVisible = false
							this.forceDeleteAuthCode = ''
							this.forceDeleteTarget = null
							this.handleQuery()
						} else {
							this.$message.error((res.data && res.data.msg) || '强制删除失败')
						}
					}).catch((error) => {
						console.error('强制删除考试失败:', error)
						this.forceDeleteLoading = false
					})
				})
				.catch(() => { })
		},
		// 删除考试
		handleDelete(id) {
			const examRow = this.exams.find(item => item.Id === id)
			if (examRow && !this.canDeleteExam(examRow)) {
				this.$message.warning(this.getEditBlockedMessage(examRow, 'exam'))
				return
			}
			this.$confirm('确认删除该考试吗？删除前请确保没有相关考试场次。', '提示', {
				type: 'warning',
			})
				.then(() => {
					this.listLoading = true
					let para = { id: id };
					deleteExam(para).then((res) => {
						this.listLoading = false
						if (res.data.success) {
							this.$message({
								message: '删除成功',
								type: 'success',
							})
							this.handleQuery()
						} else {
							this.$message({
								message: res.data.msg,
								type: 'error',
							})
						}
					}).catch(error => {
						console.error('删除考试失败:', error)
						this.listLoading = false
					})
				})
				.catch(() => { })
		},
		handleTimeChange(timeRange) {
			this.syncExamTimeFromRange(timeRange)
		},
		/** 从起止时间选择器同步 StartTime / EndTime */
		syncExamTimeFromRange(timeRange) {
			const range = timeRange || this.formData.timeRange
			if (range && range.length === 2) {
				this.formData.StartTime = this.formatDateTime(range[0])
				this.formData.EndTime = this.formatDateTime(range[1])
				this.formData.timeRange = [this.formData.StartTime, this.formData.EndTime]
			} else {
				this.formData.StartTime = ''
				this.formData.EndTime = ''
			}
		},

		// --- 考试场次相关 ---
		showExamSessions(exam) {
			this.currentExam = exam
			this.examSessionDialogTitle = `${exam.ExamName} - 考试场次管理`
			this.examSessionDialogVisible = true
			this.sessionPage = 1
			this.getCourseListByDepartmentId()
			this.getExamSessionList()
		},
		getCourseListByDepartmentId() {
			this.courseLoading = true
			const para = {
				departmentId: this.currentExam.DepartmentId || 0
			}
			GetSysCourseByDepartmentId(para).then((res) => {
				this.courseLoading = false
				if (res.data.success && res.data.response) {
					this.courseList = res.data.response
				} else {
					this.courseList = []
				}
			}).catch(error => {
				console.error('获取科目列表失败:', error)
				this.courseLoading = false
				this.courseList = []
			})
		},
		getExamSessionList() {
			let para = {
				examId: this.currentExam.Id,
				page: this.sessionPage,
				subjectName: this.subjectName,
				intPageSize: this.sessionPageSize
			}
			this.sessionListLoading = true

			getExamSessionList(para).then((res) => {
				if (res.data.success && res.data.response) {
					this.examSessions = res.data.response.data
					this.sessionTotal = res.data.response.dataCount
					this.sessionPageSize = res.data.response.PageSize
				} else {
					this.examSessions = []
					this.sessionTotal = 0
				}
				this.sessionListLoading = false
			}).catch(error => {
				console.error('获取考试场次列表失败:', error)
				this.sessionListLoading = false
				this.examSessions = []
				this.sessionTotal = 0
			})
		},
		handleSessionCurrentChange(val) {
			this.sessionPage = val
			this.getExamSessionList()
		},
		handleAddExamSession() {
			if (!this.canAddExamSession(this.currentExam)) {
				this.$message.warning(this.getEditBlockedMessage(this.currentExam, 'exam'))
				return
			}
			this.editingSessionRow = null
			this.sessionFormData = {
				Id: 0,
				ExamId: this.currentExam.Id,
				SubjectId: '',
				SubjectName: '',
				StartTime: '',
				EndTime: '',
				timeRange: [],
				Remark: ''
			}
			this.sessionFormTitle = '新增考试场次'
			this.isSessionEditMode = false
			this.sessionTimePickerKey += 1
			this.sessionFormVisible = true
		},
		handleEditExamSession(row) {
			if (!this.canEditExamSession(row, this.currentExam)) {
				this.$message.warning(this.getEditBlockedMessage(row, 'session'))
				return
			}
			this.editingSessionRow = row
			const timeRange = this.buildTimeRange(row.StartTime, row.EndTime)
			this.sessionFormData = {
				Id: row.Id,
				ExamId: row.ExamId,
				SubjectId: row.SubjectId || '',
				SubjectName: row.SubjectName || '',
				StartTime: timeRange[0] || '',
				EndTime: timeRange[1] || '',
				timeRange: timeRange,
				Remark: row.Remark || ''
			}
			this.sessionFormTitle = '编辑考试场次'
			this.isSessionEditMode = true
			this.sessionTimePickerKey += 1
			this.sessionFormVisible = true
			this.$nextTick(() => {
				if (this.$refs.sessionFormRef) {
					this.$refs.sessionFormRef.clearValidate()
				}
			})
		},
		handleSessionFormClose() {
			this.sessionFormVisible = false
			this.editingSessionRow = null
			if (this.$refs.sessionFormRef) {
				this.$refs.sessionFormRef.resetFields()
			}
		},
		handleSessionTimeChange(timeRange) {
			this.syncSessionTimeFromRange(timeRange)
			this.$nextTick(() => {
				if (this.$refs.sessionFormRef) {
					this.$refs.sessionFormRef.validateField('timeRange')
				}
			})
		},
		/** 从场次起止时间选择器同步 StartTime / EndTime */
		syncSessionTimeFromRange(timeRange) {
			const range = timeRange || this.sessionFormData.timeRange
			if (range && range.length === 2) {
				this.sessionFormData.StartTime = this.formatDateTime(range[0])
				this.sessionFormData.EndTime = this.formatDateTime(range[1])
				this.sessionFormData.timeRange = [this.sessionFormData.StartTime, this.sessionFormData.EndTime]
			} else {
				this.sessionFormData.StartTime = ''
				this.sessionFormData.EndTime = ''
			}
		},
		submitSessionForm() {
			if (!this.isSessionFormEditable) {
				this.$message.warning(this.isSessionEditMode
					? this.getEditBlockedMessage(this.editingSessionRow, 'session')
					: this.getEditBlockedMessage(this.currentExam, 'exam'))
				return
			}
			this.$refs.sessionFormRef.validate((valid) => {
				if (valid) {
					this.$confirm('确认提交场次吗？', '提示', { type: 'info' }).then(() => {
						this.syncSessionTimeFromRange()
						this.sessionSubmitLoading = true
						let para = Object.assign({}, this.sessionFormData)

						delete para.timeRange

						const selectedCourse = this.courseList.find(course => course.Id === para.SubjectId)
						if (selectedCourse) {
							para.SubjectName = selectedCourse.CourseName
						}

						const apiCall = this.isSessionEditMode ? updateExamSession : addExamSession

						apiCall(para).then((res) => {
							this.sessionSubmitLoading = false
							if (res.data.success) {
								this.$message({
									message: res.data.msg,
									type: 'success',
								})
								this.sessionFormVisible = false
								this.getExamSessionList()
								this.handleQuery();
							} else {
								this.$message({
									message: res.data.msg,
									type: 'error',
								})
							}
						}).catch(error => {
							console.error('提交考试场次失败:', error)
							this.sessionSubmitLoading = false
						})
					})
				}
			})
		},
		handleDeleteExamSession(id) {
			const sessionRow = this.examSessions.find(item => item.Id === id)
			if (sessionRow && !this.canDeleteExamSession(sessionRow, this.currentExam)) {
				this.$message.warning(this.getEditBlockedMessage(sessionRow, 'session'))
				return
			}
			this.$confirm('确认删除该考试场次吗？', '提示', {
				type: 'warning',
			})
				.then(() => {
					this.sessionListLoading = true
					let para = { id: id };
					deleteExamSession(para).then((res) => {
						this.sessionListLoading = false
						if (res.data.success) {
							this.$message({
								message: '删除成功',
								type: 'success',
							})
							this.getExamSessionList()
							this.handleQuery();
						} else {
							this.$message({
								message: res.data.msg,
								type: 'error',
							})
						}
					}).catch(error => {
						console.error('删除考试场次失败:', error)
						this.sessionListLoading = false
					})
				})
				.catch(() => { })
		},

		// 全局考场与考生编排入口
		handleGlobalRoomAssignment() {
			if (!this.canManageExamArrangement(this.currentExam)) {
				this.$message.warning(this.getEditBlockedMessage(this.currentExam, 'exam'))
				return
			}
			if (!this.examSessions || this.examSessions.length === 0) {
				this.$message.warning('请先为当前考试新增至少一个科目场次，然后再进行考场编排！')
				return
			}
			// 传入第一个场次作为基础上下文，调起对该大考试的全局考场与考生编排
			this.showRoomAssignment(this.examSessions[0])
		},

		// --- 考场编排相关 ---
		showRoomAssignment(examSession) {
			if (!this.canManageExamArrangement(this.currentExam)) {
				this.$message.warning(this.getEditBlockedMessage(this.currentExam, 'exam'))
				return
			}
			this.currentExamSession = examSession
			this.roomAssignmentDialogTitle = `考场编排 - ${examSession.ExamName}`
			this.roomAssignmentDialogVisible = true
			this.getExamSessionRooms()
			this.getAvailableExamRooms()
		},
		getExamSessionRooms() {
			// 使用大考试 ID 进行考场查询
			let para = {
				examId: this.currentExamSession.ExamId
			}
			this.roomListLoading = true

			getExamSessionRooms(para).then((res) => {
				this.roomListLoading = false
				if (res.data.success && res.data.response) {
					this.examSessionRooms = res.data.response
				} else {
					this.examSessionRooms = []
				}
			}).catch(error => {
				console.error('获取考场列表失败:', error)
				this.roomListLoading = false
				this.examSessionRooms = []
			})
		},
		getAvailableExamRooms() {
			// 使用大考试 ID 获取可用的考场列表
			let para = {
				examSessionId: this.currentExamSession.ExamId
			}
			this.availableRoomListLoading = true

			getAvailableExamRooms(para).then((res) => {
				this.availableRoomListLoading = false
				if (res.data.success && res.data.response) {
					this.availableExamRooms = res.data.response
					this.selectedRooms = []
				} else {
					this.availableExamRooms = []
				}
			}).catch(error => {
				console.error('获取可用考场列表失败:', error)
				this.availableRoomListLoading = false
				this.availableExamRooms = []
			})
		},
		assignRooms() {
			if (this.selectedRooms.length === 0) {
				this.$message.warning('请选择要分配的考场')
				return
			}

			this.$confirm('确认分配选中的考场吗？这将覆盖当前已分配的考场。', '提示', {
				confirmButtonText: '确定',
				cancelButtonText: '取消',
				type: 'warning'
			}).then(() => {
				let para = {
					// 关联大考试 ID 进行考场分配
					ExamId: this.currentExamSession.ExamId,
					ExamRoomIds: this.selectedRooms.map(room => room.Id)
				}

				assignExamSessionRooms(para).then((res) => {
					if (res.data.success) {
						this.$message({ type: 'success', message: res.data.msg })
						this.getExamSessionRooms()
						this.getAvailableExamRooms()
						this.getExamSessionList()
						this.selectedRooms = []
					} else {
						this.$message({ type: 'error', message: res.data.msg })
					}
				}).catch(error => {
					console.error('分配考场失败:', error)
				})
			}).catch(() => {})
		},
		removeRoom(examRoomId) {
			this.$confirm('确认移除该考场分配吗？', '提示', {
				confirmButtonText: '确定',
				cancelButtonText: '取消',
				type: 'warning'
			}).then(() => {
				let para = {
					// 关联大考试 ID 移除考场分配
					examId: this.currentExamSession.ExamId,
					examRoomId: examRoomId
				}

				removeExamSessionRoom(para).then((res) => {
					if (res.data.success) {
						this.$message({ type: 'success', message: res.data.msg })
						this.getExamSessionRooms()
						this.getAvailableExamRooms()
						this.getExamSessionList()
					} else {
						this.$message({ type: 'error', message: res.data.msg })
					}
				}).catch(error => {
					console.error('移除考场失败:', error)
				})
			}).catch(() => {})
		},
		handleRoomSelectionChange(val) {
			this.selectedRooms = val
		},

		// --- 监考老师相关 ---
		showRoomInvigilatorManagement(examRoom) {
			console.log('显示监考老师管理，考场数据:', examRoom)
			this.currentExamRoom = examRoom
			this.invigilatorDialogTitle = `监考老师指派 - ${examRoom.RoomName}`
			this.invigilatorDialogVisible = true
			this.getExamRoomInvigilatorList()
			this.getAvailableInvigilators()
		},
		getExamRoomInvigilatorList() {
			let para = {
				examRoomId: this.currentExamRoom.ExamRoomId,
				examId: this.currentExamRoom.ExamSessionId
			}
			this.invigilatorListLoading = true

			getExamInvigilatorList(para).then((res) => {
				this.invigilatorListLoading = false
				if (res.data.success && res.data.response) {
					this.examInvigilators = res.data.response
				} else {
					this.examInvigilators = []
				}
			}).catch(error => {
				console.error('获取监考老师列表失败:', error)
				this.invigilatorListLoading = false
				this.examInvigilators = []
			})
		},
		getAvailableInvigilators() {
			// 关联大考试 ID 获取本考场可用的监考老师
			let para = {
				examId: this.currentExamSession.ExamId,
				examRoomId: this.currentExamRoom.ExamRoomId
			}
			this.availableInvigilatorListLoading = true

			getAvailableInvigilators(para).then((res) => {
				this.availableInvigilatorListLoading = false
				if (res.data.success && res.data.response) {
					this.availableInvigilators = res.data.response
					this.selectedInvigilators = []
				} else {
					this.availableInvigilators = []
				}
			}).catch(error => {
				console.error('获取可用监考老师失败:', error)
				this.availableInvigilatorListLoading = false
				this.availableInvigilators = []
			})
		},
		assignInvigilators() {
			if (this.selectedInvigilators.length === 0) {
				this.$message.warning('请选择要指派的监考老师')
				return
			}

			this.$confirm('确认分配选中的监考老师吗？', '提示', {
				confirmButtonText: '确定',
				cancelButtonText: '取消',
				type: 'warning'
			}).then(() => {
				let para = {
					// 关联大考试 ID 指派监考老师
					ExamId: this.currentExamSession.ExamId,
					ExamRoomId: this.currentExamRoom.ExamRoomId,
					TeacherIds: this.selectedInvigilators.map(teacher => teacher.Id)
				}
				assignInvigilator(para).then((res) => {
					if (res.data.success) {
						this.$message({ type: 'success', message: res.data.msg })
						this.getExamRoomInvigilatorList()
						this.getAvailableInvigilators()
						this.selectedInvigilators = []
					} else {
						this.$message({ type: 'error', message: res.data.msg })
					}
				}).catch(error => {
					console.error('分配监考老师失败:', error)
				})
			}).catch(() => {})
		},
		removeExamInvigilator(invigilatorId) {
			this.$confirm('确认移除该监考老师吗？', '提示', {
				confirmButtonText: '确定',
				cancelButtonText: '取消',
				type: 'warning'
			}).then(() => {
				let para = {
					id: invigilatorId
				}

				removeInvigilator(para).then((res) => {
					if (res.data.success) {
						this.$message({ type: 'success', message: res.data.msg })
						this.getExamRoomInvigilatorList()
						this.getAvailableInvigilators()
					} else {
						this.$message({ type: 'error', message: res.data.msg })
					}
				}).catch(error => {
					console.error('移除监考老师失败:', error)
				})
			}).catch(() => {})
		},
		handleInvigilatorSelectionChange(val) {
			this.selectedInvigilators = val
		},
		/** 判断右侧可选老师卡片是否已被选中 */
		isInvigilatorSelected(item) {
			return this.selectedInvigilators.some(t => t.Id === item.Id)
		},
		/** 点击右侧卡片切换选中状态 */
		toggleInvigilatorSelect(item) {
			const idx = this.selectedInvigilators.findIndex(t => t.Id === item.Id)
			if (idx >= 0) {
				this.selectedInvigilators.splice(idx, 1)
			} else {
				this.selectedInvigilators.push(item)
			}
		},
		showAllInvigilators(invigilatorNames) {
			this.allInvigilators = (invigilatorNames || '').split(',').filter(item => item.trim());
			this.allInvigilatorsDialogTitle = `全部监考老师`;
			this.showAllInvigilatorsDialog = true;
		},

		// --- 考生安排相关 ---
		showStudentAssignment(examRoom) {
			this.availableStudentPage = 1
			this.availableStudentPageSize = 10
			this.availableStudentTotal = 0
			this.examStudentPage = 1
			this.examStudentPageSize = 10
			this.examStudentTotal = 0
			this.selectedExamStudents = []
			this.selectedStudents = []
			this.currentExamRoomForStudent = examRoom
			this.studentAssignmentDialogTitle = `考生分配安排 - ${examRoom.RoomName}`
			this.studentAssignmentDialogVisible = true
			this.getExamStudentList()
			this.getAvailableStudents()
		},
		getExamStudentList() {
			// 关联大考试 ID 获取已编排的考生列表
			let para = {
				examId: this.currentExamSession.ExamId,
				examRoomId: this.currentExamRoomForStudent.ExamRoomId,
				page: this.examStudentPage,
				pageSize: this.examStudentPageSize
			}
			this.studentListLoading = true

			getExamStudentList(para).then((res) => {
				this.studentListLoading = false
				if (res.data.success && res.data.response) {
					this.examStudentTotal = res.data.response.dataCount;
					this.examStudents = res.data.response.data;
					this.examStudentPageSize = res.data.response.PageSize;
				} else {
					this.examStudents = []
					this.examStudentTotal = 0
				}
			}).catch(error => {
				console.error('获取考生列表失败:', error)
				this.studentListLoading = false
				this.examStudents = []
				this.examStudentTotal = 0
			})
		},
		getAvailableStudents() {
			// 关联大考试 ID 获取待分配考生列表
			let para = {
				examId: this.currentExamSession.ExamId,
				examRoomId: this.currentExamRoomForStudent.ExamRoomId,
				page: this.availableStudentPage,
				pageSize: this.availableStudentPageSize
			}
			this.availableStudentListLoading = true

			getAvailableStudents(para).then((res) => {
				this.availableStudentListLoading = false

				if (res.data.success && res.data.response) {
					this.availableStudentTotal = res.data.response.dataCount;
					this.availableStudents = res.data.response.data;
					this.availableStudentPageSize = res.data.response.PageSize;
				} else {
					this.availableStudents = []
				}
			}).catch(error => {
				console.error('获取可用考生列表失败:', error)
				this.availableStudentListLoading = false
				this.availableStudents = []
			})
		},
		async assignStudents() {
			if (this.selectedStudents.length === 0) {
				this.$message.warning('请选择要分配的考生')
				return
			}

			this.seatPreviewMode = 'import'
			this.studentListLoading = true
			try {
				const resArranged = await getArrangedStudentsByExamId({ examId: this.currentExamSession.ExamId })
				let occupiedSeats = new Set()
				let existingStudents = []
				if (resArranged.data && resArranged.data.success) {
					const rooms = resArranged.data.response || []
					const currentRoom = rooms.find(r => r.ExamRoomId === this.currentExamRoomForStudent.ExamRoomId)
					existingStudents = currentRoom ? (currentRoom.Students || []) : []
					existingStudents.forEach(s => {
						if (s.SeatNumber) {
							occupiedSeats.add(Number(s.SeatNumber))
						}
					})
				}
				this.occupiedSeatNumbers = occupiedSeats
				this.existingRoomSeatStudents = existingStudents.map(s => ({
					StudentId: Number(s.StudentId),
					StuName: s.StuName || '',
					StuCode: s.StuCode || '',
					DepartmentName: s.DepartmentName || '',
					SeatNumber: Number(s.SeatNumber)
				}))

				const capacityCheck = this.validateImportSeatCapacity(this.selectedStudents.length)
				if (!capacityCheck.ok) {
					this.$message.warning(
						`考场剩余空位不足：容量 ${capacityCheck.capacity}，已占用 ${capacityCheck.lockedCount}，仅剩 ${capacityCheck.emptyCount} 个空位，无法导入 ${capacityCheck.importCount} 名考生`
					)
					return
				}

				this.previewNewStudents = this.selectedStudents.map((s, index) => ({
					uniqueId: `${s.StudentId || s.Id || index}_${index}`,
					StudentId: Number(s.StudentId || s.Id),
					StuName: String(s.StuName || ''),
					StuCode: String(s.StuCode || s.CardNumber || ''),
					DepartmentId: Number(s.DepartmentId || 0),
					DepartmentName: String(s.DepartmentName || '-'),
					Sex: s.Sex === 0 || s.Sex === '0' ? 0 : (s.Sex === 1 || s.Sex === '1' ? 1 : -1),
					ClassSeatNumber: Number(s.ClassSeatNumber || s.classSeatNumber || 0),
					SeatNumber: null,
					HeadPhotoImage: s.HeadPhotoImage || ''
				}))
				this.previewSelectionOrder = {}
				this.previewNewStudents.forEach((s, index) => {
					this.previewSelectionOrder[s.StudentId] = index
				})

				this.applySeatSortRule('sequential', { silent: true })
				this.seatDragSource = null
				this.importPreviewDialogVisible = true
			} catch (e) {
				console.error('准备分配预览失败:', e)
				this.$message.error('准备分配数据发生错误')
			} finally {
				this.studentListLoading = false
			}
		},
		/** 打开座位图，调整已分配考生的座位 */
		async openSeatMapEditor() {
			if (!this.examStudentTotal) {
				this.$message.warning('当前考场暂无已分配考生')
				return
			}

			this.studentListLoading = true
			try {
				const res = await getExamStudentList({
					examId: this.currentExamSession.ExamId,
					examRoomId: this.currentExamRoomForStudent.ExamRoomId,
					page: 1,
					pageSize: Math.max(this.examStudentTotal, 500)
				})
				if (!res.data || !res.data.success || !res.data.response) {
					this.$message.error('获取已分配考生失败')
					return
				}

				const list = res.data.response.data || []
				if (!list.length) {
					this.$message.warning('当前考场暂无已分配考生')
					return
				}

				this.seatPreviewMode = 'edit'
				this.existingRoomSeatStudents = []
				this.occupiedSeatNumbers = new Set()
				this.previewNewStudents = list.map((s, index) => ({
					uniqueId: `edit_${s.Id || s.StudentId}_${index}`,
					examStudentId: Number(s.Id),
					StudentId: Number(s.StudentId),
					StuName: String(s.StuName || ''),
					StuCode: String(s.StuCode || ''),
					DepartmentId: 0,
					DepartmentName: String(s.DepartmentName || '-'),
					Sex: -1,
					ClassSeatNumber: 0,
					SeatNumber: Number(s.SeatNumber) || null,
					HeadPhotoImage: s.HeadPhotoImage || ''
				}))
				this.previewSelectionOrder = {}
				this.previewNewStudents.forEach((s, index) => {
					this.previewSelectionOrder[s.StudentId] = index
				})

				this.applySeatSortRule('sequential', { silent: true })
				this.seatDragSource = null
				this.importPreviewDialogVisible = true
			} catch (e) {
				console.error('打开座位图调整失败:', e)
				this.$message.error('加载座位数据失败')
			} finally {
				this.studentListLoading = false
			}
		},
		/** 获取考场可分配给新考生的空座位号 */
		getAssignableEmptySeats() {
			const { capacity } = this.previewSeatLayoutMeta
			const lockedSeats = new Set(
				(this.existingRoomSeatStudents || [])
					.map(s => Number(s.SeatNumber))
					.filter(n => n > 0)
			)
			const emptySeats = []
			for (let i = 1; i <= capacity; i++) {
				if (!lockedSeats.has(i)) {
					emptySeats.push(i)
				}
			}
			return emptySeats
		},
		/** 校验导入考生时考场空位是否足够 */
		validateImportSeatCapacity(importCount) {
			const count = Number(importCount) || 0
			const emptySeats = this.getAssignableEmptySeats()
			const { capacity } = this.previewSeatLayoutMeta
			const lockedCount = Math.max(capacity - emptySeats.length, 0)
			if (emptySeats.length >= count) {
				return {
					ok: true,
					emptyCount: emptySeats.length,
					capacity,
					lockedCount,
					importCount: count
				}
			}
			return {
				ok: false,
				emptyCount: emptySeats.length,
				capacity,
				lockedCount,
				importCount: count
			}
		},
		/** S 形（蛇形）座位遍历顺序 */
		getSnakeSeatOrder(rows, cols) {
			const order = []
			for (let r = 0; r < rows; r++) {
				if (r % 2 === 0) {
					for (let c = 0; c < cols; c++) {
						order.push(r * cols + c + 1)
					}
				} else {
					for (let c = cols - 1; c >= 0; c--) {
						order.push(r * cols + c + 1)
					}
				}
			}
			return order
		},
		/** 随机打乱数组（Fisher-Yates） */
		shuffleStudentList(list) {
			const arr = [...list]
			for (let i = arr.length - 1; i > 0; i--) {
				const j = Math.floor(Math.random() * (i + 1))
				const temp = arr[i]
				arr[i] = arr[j]
				arr[j] = temp
			}
			return arr
		},
		/** 按班级分组后轮询穿插（混班防作弊） */
		interleaveStudentsByClass(students) {
			const groupMap = {}
			const groupOrder = []
			students.forEach(student => {
				const key = student.DepartmentId ? String(student.DepartmentId) : (student.DepartmentName || '未知班级')
				if (!groupMap[key]) {
					groupMap[key] = []
					groupOrder.push(key)
				}
				groupMap[key].push(student)
			})
			const result = []
			let hasMore = true
			let index = 0
			while (hasMore) {
				hasMore = false
				groupOrder.forEach(key => {
					if (groupMap[key][index]) {
						result.push(groupMap[key][index])
						hasMore = true
					}
				})
				index++
			}
			return result
		},
		/** 男女交替排列（1男0女，未知性别排后） */
		alternateStudentsByGender(students) {
			const males = students.filter(s => s.Sex === 1)
			const females = students.filter(s => s.Sex === 0)
			const others = students.filter(s => s.Sex !== 0 && s.Sex !== 1)
			const result = []
			const maxLen = Math.max(males.length, females.length)
			for (let i = 0; i < maxLen; i++) {
				if (males[i]) result.push(males[i])
				if (females[i]) result.push(females[i])
			}
			return result.concat(others)
		},
		/** 获取座位填充顺序（Z形 / S形） */
		getSeatFillOrder(rule, emptySeats) {
			if (rule === 'snake') {
				const { rows, cols } = this.previewSeatLayoutMeta
				const snakeOrder = this.getSnakeSeatOrder(rows, cols)
				return snakeOrder.filter(seat => emptySeats.includes(seat))
			}
			return [...emptySeats].sort((a, b) => a - b)
		},
		/** 各排号规则说明（统一从 seatSortRuleGroups 读取） */
		getSeatSortRuleMeta(rule) {
			for (let g = 0; g < this.seatSortRuleGroups.length; g++) {
				const group = this.seatSortRuleGroups[g]
				for (let i = 0; i < group.rules.length; i++) {
					const item = group.rules[i]
					if (item.key === rule) {
						return { title: item.label, tip: item.tip }
					}
				}
			}
			return { title: '排号', tip: '已应用排号规则。' }
		},
		/** 快捷排号规则 */
		applySeatSortRule(rule, options = {}) {
			if (!this.previewNewStudents || this.previewNewStudents.length === 0) return false

			let students = [...this.previewNewStudents]
			if (rule === 'sequential') {
				students.sort((a, b) => (this.previewSelectionOrder[a.StudentId] || 0) - (this.previewSelectionOrder[b.StudentId] || 0))
			} else if (rule === 'stuCode') {
				students.sort((a, b) => String(a.StuCode || '').localeCompare(String(b.StuCode || ''), 'zh-CN', { numeric: true }))
			} else if (rule === 'stuCodeDesc') {
				students.sort((a, b) => String(b.StuCode || '').localeCompare(String(a.StuCode || ''), 'zh-CN', { numeric: true }))
			} else if (rule === 'name') {
				students.sort((a, b) => String(a.StuName || '').localeCompare(String(b.StuName || ''), 'zh-CN'))
			} else if (rule === 'classStuCode') {
				students.sort((a, b) => {
					const classCmp = String(a.DepartmentName || '').localeCompare(String(b.DepartmentName || ''), 'zh-CN')
					if (classCmp !== 0) return classCmp
					return String(a.StuCode || '').localeCompare(String(b.StuCode || ''), 'zh-CN', { numeric: true })
				})
			} else if (rule === 'classSeat') {
				students.sort((a, b) => {
					const classCmp = String(a.DepartmentName || '').localeCompare(String(b.DepartmentName || ''), 'zh-CN')
					if (classCmp !== 0) return classCmp
					const seatA = Number(a.ClassSeatNumber) || 9999
					const seatB = Number(b.ClassSeatNumber) || 9999
					return seatA - seatB
				})
			} else if (rule === 'classInterleave') {
				students = this.interleaveStudentsByClass(students)
			} else if (rule === 'genderAlternate') {
				students = this.alternateStudentsByGender(students)
			} else if (rule === 'random') {
				students = this.shuffleStudentList(students)
			}

			const emptySeats = this.getAssignableEmptySeats()
			const seatPathRule = rule === 'snake' ? 'snake' : 'zShape'
			const finalSeatOrder = this.getSeatFillOrder(seatPathRule, emptySeats)

			if (finalSeatOrder.length < students.length) {
				if (!options.silent) {
					this.$message.warning('考场剩余空位不足，无法为全部考生排座')
				}
				return false
			}

			students.forEach((student, index) => {
				student.SeatNumber = finalSeatOrder[index] || null
			})
			this.previewNewStudents = students

			const meta = this.getSeatSortRuleMeta(rule)
			this.activeSeatSortRule = rule
			this.activeSeatSortRuleTitle = meta.title
			this.seatRuleTip = meta.tip
			if (!options.silent && !options.skipToast) {
				this.$message({ type: 'success', message: `已应用「${meta.title}」`, duration: 2000 })
			}
			return true
		},
		/** 座位图单元格样式 */
		previewSeatCellClass(cell) {
			return {
				'seat-cell': true,
				'is-empty': !cell.student,
				'is-locked': cell.isLocked && !!cell.student,
				'is-new': cell.isNew && !!cell.student,
				'is-duplicate': this.duplicateSeatNumbers.has(cell.seatNumber),
				'is-drag-over': this.seatDragSource && this.seatDragSource.seatNumber !== cell.seatNumber
			}
		},
		onPreviewSeatDragStart(cell, event) {
			if (!cell.isNew || !cell.student) {
				event.preventDefault()
				return
			}
			this.seatDragSource = {
				seatNumber: cell.seatNumber,
				studentId: cell.student.StudentId
			}
			if (event && event.dataTransfer) {
				event.dataTransfer.effectAllowed = 'move'
			}
		},
		onPreviewSeatDragOver(cell, event) {
			if (cell.isLocked) return
			if (event && event.dataTransfer) {
				event.dataTransfer.dropEffect = 'move'
			}
		},
		onPreviewSeatDrop(targetCell) {
			const dragSource = this.seatDragSource
			if (!dragSource) return
			if (targetCell.isLocked) {
				this.$message.warning('该座位已被其他考生占用')
				return
			}

			const sourceStudent = this.previewNewStudents.find(s => s.StudentId === dragSource.studentId)
			if (!sourceStudent) return

			if (targetCell.student && targetCell.isNew) {
				const targetStudent = this.previewNewStudents.find(s => s.StudentId === targetCell.student.StudentId)
				if (targetStudent) {
					const tempSeat = targetStudent.SeatNumber
					targetStudent.SeatNumber = sourceStudent.SeatNumber
					sourceStudent.SeatNumber = tempSeat
				}
			} else if (!targetCell.student) {
				sourceStudent.SeatNumber = targetCell.seatNumber
			}

			this.seatDragSource = null
			this.previewNewStudents = [...this.previewNewStudents]
		},
		confirmAssignWithSeats() {
			if (this.hasDuplicateSeats) {
				this.$message.error('请确保每位考生都有唯一、未占用的座位号后再提交')
				return
			}
			if (this.seatPreviewMode === 'edit') {
				this.confirmEditSeats()
				return
			}
			this.assignLoading = true
			let para = {
				ExamId: this.currentExamSession.ExamId,
				ExamRoomId: this.currentExamRoomForStudent.ExamRoomId,
				StudentIds: this.previewNewStudents.map(student => student.StudentId),
				SeatAssignments: this.previewNewStudents.map(student => ({
					StudentId: student.StudentId,
					SeatNumber: Number(student.SeatNumber)
				}))
			}

			assignExamStudents(para).then((res) => {
				if (res.data.success) {
					this.$message({ type: 'success', message: '考生分配并排定座位号成功！' })
					this.importPreviewDialogVisible = false
					this.getExamStudentList()
					this.getAvailableStudents()
					this.selectedStudents = []
					this.getExamSessionRooms()
					this.handleQuery()
				} else {
					this.$message.error(res.data.msg || '分配考生失败')
				}
				this.assignLoading = false
			}).catch(error => {
				console.error('分配考生请求失败:', error)
				this.$message.error('请求接口发生错误，请稍后重试')
				this.assignLoading = false
			})
		},
		/** 保存已分配考生的座位图调整 */
		async confirmEditSeats() {
			this.assignLoading = true
			try {
				const updatePromises = this.previewNewStudents.map(student => {
					return updateExamStudent({
						Id: student.examStudentId,
						StudentId: student.StudentId,
						ExamId: this.currentExamSession.ExamId,
						ExamRoomId: this.currentExamRoomForStudent.ExamRoomId,
						SeatNumber: Number(student.SeatNumber)
					})
				})
				const results = await Promise.all(updatePromises)
				const failed = results.find(res => !res.data || !res.data.success)
				if (failed) {
					this.$message.error((failed.data && failed.data.msg) || '部分座位保存失败')
					return
				}
				this.$message({ type: 'success', message: '座位调整已保存！' })
				this.importPreviewDialogVisible = false
				this.getExamStudentList()
				this.getExamSessionRooms()
				this.handleQuery()
			} catch (error) {
				console.error('保存座位调整失败:', error)
				this.$message.error('保存座位调整失败，请稍后重试')
			} finally {
				this.assignLoading = false
			}
		},
		removeExamStudent(row) {
			this.$confirm('确认从当前考场移除该考生安排吗？', '提示', {
				confirmButtonText: '确定',
				cancelButtonText: '取消',
				type: 'warning'
			}).then(() => {
				let para = {
					id: row.Id
				}
				removeExamStudent(para).then((res) => {
					if (res.data.success) {
						this.$message({ type: 'success', message: res.data.msg })
						this.getExamStudentList()
						this.getAvailableStudents()
						this.getExamSessionRooms()
						this.handleQuery();
					} else {
						this.$message({ type: 'error', message: res.data.msg })
					}
				}).catch(error => {
					console.error('移除考生失败:', error)
				})
			}).catch(() => {})
		},
		// 修改考生座位号与智能冲突对调逻辑
		handleSeatNumberChange(row, val) {
			if (!val) return
			
			this.studentListLoading = true
			getArrangedStudentsByExamId({ examId: this.currentExamSession.ExamId }).then(async (resArranged) => {
				if (resArranged.data && resArranged.data.success) {
					const rooms = resArranged.data.response || []
					const currentRoom = rooms.find(r => r.ExamRoomId === this.currentExamRoomForStudent.ExamRoomId)
					const students = currentRoom ? (currentRoom.Students || []) : []
					
					// 1. 找到当前被修改学生在数据库中的旧记录（获取旧座位号）
					const dbSelf = students.find(s => s.StudentId === row.StudentId)
					const oldSeat = dbSelf && dbSelf.SeatNumber ? Number(dbSelf.SeatNumber) : null
					
					if (dbSelf) {
						// 2. 检查是否有其他考生占用了新的目标座位号
						const duplicateStudent = students.find(s => s.SeatNumber && Number(s.SeatNumber) === Number(val) && s.StudentId !== row.StudentId)
						
						if (duplicateStudent) {
							// 存在冲突，弹出确认对调框
							this.$confirm(`座位号 ${val} 已被考生【${duplicateStudent.StuName}】占用，是否将【${row.StuName}】与【${duplicateStudent.StuName}】的座位号进行对调？`, '座位号冲突提示', {
								confirmButtonText: '确定对调',
								cancelButtonText: '取消',
								type: 'warning'
							}).then(async () => {
								try {
									// 分步更新：先将对方修改为安全的临时占位值（如9999），再改当前学生，最后改回对方，避免数据库唯一索引冲突
									await updateExamStudent({ ...duplicateStudent, SeatNumber: 9999 })
									await updateExamStudent({ ...dbSelf, SeatNumber: val })
									await updateExamStudent({ ...duplicateStudent, SeatNumber: oldSeat || 999 })
									
									this.$message({
										type: 'success',
										message: `已成功对调【${row.StuName}】与【${duplicateStudent.StuName}】的座位！`
									})
								} catch (e) {
									console.error('对调座位失败:', e)
									this.$message.error('对调座位失败，数据已回滚')
								} finally {
									this.getExamStudentList()
									this.getExamSessionRooms()
								}
							}).catch(() => {
								// 取消对调，直接重载数据恢复原状
								this.getExamStudentList()
							})
						} else {
							// 目标座位无人占用，直接修改当前考生座位号
							updateExamStudent({ ...dbSelf, SeatNumber: val }).then(res => {
								if (res.data.success) {
									this.$message.success('座位号修改成功')
									this.getExamStudentList()
									this.getExamSessionRooms()
								} else {
									this.$message.error(res.data.msg || '修改座位号失败')
									this.getExamStudentList()
								}
							}).catch(error => {
								console.error('修改座位号失败:', error)
								this.$message.error('保存座位号失败，请稍后重试')
								this.getExamStudentList()
							})
						}
					} else {
						this.$message.error('找不到该考生在当前考场的安排记录')
						this.getExamStudentList()
					}
				} else {
					this.$message.error('无法验证该座位是否被占用，请稍后重试')
					this.getExamStudentList()
				}
			}).catch(err => {
				console.error('获取考场座位编排失败:', err)
				this.$message.error('座位校验失败')
				this.getExamStudentList()
			}).finally(() => {
				this.studentListLoading = false
			})
		},
		getAvatarText(name) {
			if (!name) return ''
			return name.length > 2 ? name.substring(name.length - 2) : name
		},
		getStudentAvatarStyle(name) {
			if (!name) name = '学生'
			let hash = 0
			for (let i = 0; i < name.length; i++) {
				hash = name.charCodeAt(i) + ((hash << 5) - hash)
			}
			const h = Math.abs(hash % 360)
			return {
				backgroundColor: `hsl(${h}, 65%, 90%)`,
				color: `hsl(${h}, 70%, 35%)`
			}
		},
		getStudentAvatarUrl(path) {
			if (!path) return ''
			const firstPath = path.split(',')[0].trim()
			if (!firstPath) return ''
			if (firstPath.startsWith('http')) return firstPath
			const base = BaseApiUrl || ''
			const hasBaseSlash = base.endsWith('/')
			const hasPathSlash = firstPath.startsWith('/')
			if (!hasBaseSlash && !hasPathSlash) {
				return base + '/' + firstPath
			} else if (hasBaseSlash && hasPathSlash) {
				return base + firstPath.substring(1)
			}
			return base + firstPath
		},
		handleStudentSelectionChange(val) {
			this.selectedStudents = val
		},
		handleExamStudentSelectionChange(val) {
			this.selectedExamStudents = val
		},
		/** 获取考生行唯一标识 */
		getStudentRowKey(row) {
			return row.StudentId || row.Id
		},
		/** 判断已分配考生行是否已勾选 */
		isExamStudentRowSelected(row) {
			const rowId = this.getStudentRowKey(row)
			return this.selectedExamStudents.some(item => this.getStudentRowKey(item) === rowId)
		},
		/** 切换已分配考生行勾选 */
		toggleExamStudentRowSelect(row, checked) {
			const rowId = this.getStudentRowKey(row)
			if (checked) {
				if (!this.isExamStudentRowSelected(row)) {
					this.selectedExamStudents = [...this.selectedExamStudents, row]
				}
				return
			}
			this.selectedExamStudents = this.selectedExamStudents.filter(
				item => this.getStudentRowKey(item) !== rowId
			)
		},
		/** 点击行切换已分配考生勾选 */
		handleExamStudentRowClick(row) {
			this.toggleExamStudentRowSelect(row, !this.isExamStudentRowSelected(row))
		},
		/** 全选/取消全选已分配考生 */
		toggleAllExamStudents(checked) {
			this.selectedExamStudents = checked ? [...this.examStudents] : []
		},
		/** 判断待分配考生行是否已勾选 */
		isPendingStudentRowSelected(row) {
			const rowId = this.getStudentRowKey(row)
			return this.selectedStudents.some(item => this.getStudentRowKey(item) === rowId)
		},
		/** 切换待分配考生行勾选 */
		togglePendingStudentRowSelect(row, checked) {
			const rowId = this.getStudentRowKey(row)
			if (checked) {
				if (!this.isPendingStudentRowSelected(row)) {
					this.selectedStudents = [...this.selectedStudents, row]
				}
				return
			}
			this.selectedStudents = this.selectedStudents.filter(
				item => this.getStudentRowKey(item) !== rowId
			)
		},
		/** 点击行切换待分配考生勾选 */
		handlePendingStudentRowClick(row) {
			this.togglePendingStudentRowSelect(row, !this.isPendingStudentRowSelected(row))
		},
		/** 合并待分配考生选中项（按 StudentId 去重） */
		mergeSelectedPendingStudents(students) {
			const selectedMap = new Map()
			this.selectedStudents.forEach(item => {
				selectedMap.set(this.getStudentRowKey(item), item)
			})
			;(students || []).forEach(item => {
				selectedMap.set(this.getStudentRowKey(item), item)
			})
			this.selectedStudents = Array.from(selectedMap.values())
		},
		/** 全选/取消全选当前页待分配考生 */
		toggleAllPendingStudentsOnPage(checked) {
			if (checked) {
				this.mergeSelectedPendingStudents(this.availableStudents)
				return
			}
			const pageIdSet = new Set(this.availableStudents.map(item => this.getStudentRowKey(item)))
			this.selectedStudents = this.selectedStudents.filter(
				item => !pageIdSet.has(this.getStudentRowKey(item))
			)
		},
		/** 一键全选/取消全选全部待分配考生 */
		async selectAllPendingStudentsAcrossPages() {
			if (!this.availableStudentTotal) return

			if (this.isAllPendingStudentsAcrossPagesSelected) {
				this.clearPendingStudentSelection()
				return
			}

			this.pendingSelectAllLoading = true
			try {
				const res = await getAvailableStudents({
					examId: this.currentExamSession.ExamId,
					examRoomId: this.currentExamRoomForStudent.ExamRoomId,
					page: 1,
					pageSize: this.availableStudentTotal
				})
				if (res.data.success && res.data.response) {
					const allStudents = res.data.response.data || []
					this.mergeSelectedPendingStudents(allStudents)
					this.$message.success(`已全选 ${this.selectedStudents.length} 名考生`)
				} else {
					this.$message.error('获取全部待分配考生失败')
				}
			} catch (error) {
				console.error('全选待分配考生失败:', error)
				this.$message.error('全选失败，请稍后重试')
			} finally {
				this.pendingSelectAllLoading = false
			}
		},
		/** 清空待分配考生选中项 */
		clearPendingStudentSelection() {
			this.selectedStudents = []
		},
		removeSelectedExamStudents() {
			if (this.selectedExamStudents.length === 0) return

			this.$confirm(`确认要将选中的 ${this.selectedExamStudents.length} 名考生移出当前考场吗？`, '提示', {
				confirmButtonText: '确定',
				cancelButtonText: '取消',
				type: 'warning'
			}).then(() => {
				const loading = this.$loading({
					lock: true,
					text: '正在批量移除考生，请稍候',
					background: 'rgba(0, 0, 0, 0.7)',
				})

				// 并发请求移除关系
				const promises = this.selectedExamStudents.map(student => {
					return removeExamStudent({ id: student.Id })
				})

				Promise.all(promises).then((results) => {
					loading.close()
					let successCount = 0
					results.forEach(res => {
						if (res.data && res.data.success) {
							successCount++
						}
					})

					this.$message({
						type: successCount === results.length ? 'success' : 'warning',
						message: `成功移除 ${successCount} 名考生`
					})

					// 重新刷新数据
					this.getExamStudentList()
					this.getAvailableStudents()
					this.getExamSessionRooms()
					this.handleQuery()
					this.selectedExamStudents = []
				}).catch(error => {
					loading.close()
					console.error('批量移除考生失败:', error)
					this.$message.error('部分或全部考生移除失败')
					this.getExamStudentList()
					this.getAvailableStudents()
					this.getExamSessionRooms()
					this.handleQuery()
					this.selectedExamStudents = []
				})
			}).catch(() => {})
		},
		clearAllExamStudents() {
			if (this.examStudentTotal === 0) return

			this.$confirm(`确认要将当前考场的所有 ${this.examStudentTotal} 名考生全部清空移出吗？此操作不可逆。`, '提示', {
				confirmButtonText: '确定',
				cancelButtonText: '取消',
				type: 'warning'
			}).then(() => {
				const loading = this.$loading({
					lock: true,
					text: '正在清空当前考场考生，请稍候',
					background: 'rgba(0, 0, 0, 0.7)',
				})

				// 获取该考场下所有已编排的学生列表（获取大批量不限分页数据）
				let para = {
					examId: this.currentExamSession.ExamId,
					examRoomId: this.currentExamRoomForStudent.ExamRoomId,
					page: 1,
					pageSize: 9999
				}

				getExamStudentList(para).then((res) => {
					if (res.data.success && res.data.response && res.data.response.data) {
						const allStudents = res.data.response.data
						if (allStudents.length === 0) {
							loading.close()
							this.$message.info('当前考场没有已分配的考生')
							return
						}

						// 并发请求批量移出
						const promises = allStudents.map(student => {
							return removeExamStudent({ id: student.Id })
						})

						Promise.all(promises).then((results) => {
							loading.close()
							let successCount = 0
							results.forEach(r => {
								if (r.data && r.data.success) successCount++
							})

							this.$message.success(`成功移出 ${successCount} 名考生，考场已清空`)

							// 刷新数据
							this.getExamStudentList()
							this.getAvailableStudents()
							this.getExamSessionRooms()
							this.handleQuery()
							this.selectedExamStudents = []
						}).catch(err => {
							loading.close()
							console.error('一键清空考场失败:', err)
							this.$message.error('清空过程中发生错误')
							this.getExamStudentList()
							this.getAvailableStudents()
							this.getExamSessionRooms()
							this.handleQuery()
						})
					} else {
						loading.close()
						this.$message.error('获取考场全量学生数据失败')
					}
				}).catch(error => {
					loading.close()
					console.error('获取考生全量数据失败:', error)
					this.$message.error('读取考生列表失败，清空取消')
				})
			}).catch(() => {})
		},
		handleAvailableStudentSizeChange(pageSize) {
			this.availableStudentPageSize = pageSize
			this.availableStudentPage = 1
			this.getAvailableStudents()
		},
		handleAvailableStudentCurrentChange(current) {
			this.availableStudentPage = current
			this.getAvailableStudents()
		},
		handleExamStudentSizeChange(pageSize) {
			this.examStudentPageSize = pageSize
			this.examStudentPage = 1
			this.getExamStudentList()
		},
		handleExamStudentCurrentChange(current) {
			this.examStudentPage = current
			this.getExamStudentList()
		},

		// --- 导入处理 ---
		ImportCensus() {
			// 初始化导入步骤与数据，并显示导入弹窗
			this.importStep = 1
			this.outputs = []
			this.excelImportDialogVisible = true
		},
		downloadTemplateFile() {
			// 获取考生模板下载地址并打开，触发流式下载
			const downloadUrl = downloadStudentTemplate();
			window.open(downloadUrl, '_blank');
		},
		triggerFileInput() {
			// 模拟点击隐藏的上传 input 元素以选择文件
			this.$refs.upload.click();
		},
		removeImport(e) {
			this.$confirm('确定要删除' + e.row.StudentName + '的导入行数据吗？').then(() => {
				this.outputs.splice(e['$index'], 1)
			})
		},
		SavaImport() {
			const loading = this.$loading({
				lock: true,
				text: '正在导入考生数据，请稍候',
				background: 'rgba(0, 0, 0, 0.7)',
			})

			importExamStudentList(this.outputs)
				.then((res) => {
					loading.close()
					// 无论是否完全成功，只要返回结果都渲染到 Step 3 呈现明细
					if (res.data && res.data.response) {
						this.importResult = res.data.response
						this.importStep = 3
						this.handleQuery() // 刷新列表数据
					} else {
						// 兜底错误提示
						this.$message({
							message: res.data.msg || '导入服务异常',
							type: 'error',
							duration: 0,
							showClose: true
						})
					}
				})
				.catch((e) => {
					loading.close()
					this.$message({
						message: '导入失败，请检查服务连接及数据格式',
						type: 'error',
					})
				})
		},
		readExcel(e) {
			var that = this
			const files = e.target.files
			if (files.length <= 0) {
				return false
			} else if (!/\.(xls|xlsx)$/.test(files[0].name.toLowerCase())) {
				this.$message.error('格式不正确，请上传 xls 或 xlsx 格式的 Excel 表格')
				return false
			}

			const loading = this.$loading({
				lock: true,
				text: '读取文件中...',
				background: 'rgba(0, 0, 0, 0.7)',
			})

			const fileReader = new FileReader()
			fileReader.onload = (ev) => {
				try {
					const data = ev.target.result
					const workbook = read(data, { type: 'binary' })
					const wsname = workbook.SheetNames[0]
					const sheet = workbook.Sheets[wsname]
					let ws = utils.sheet_to_json(sheet)
					const firstKey = Object.keys(ws[0] || {})[0] || ''
					if (firstKey.indexOf('填写说明') >= 0 || firstKey.indexOf('考试名称') < 0) {
						ws = utils.sheet_to_json(sheet, { range: 1 })
					}
					loading.close()
					this.outputs = this.reSetKey(ws)
					this.$nextTick(() => {
						this.importStep = 2
						this.$refs.upload.value = ''
					})
				} catch (e) {
					console.log(e)
					loading.close()
					this.$message.error('Excel 文件读取解析失败')
					return false
				}
			}
			fileReader.readAsBinaryString(files[0])
		},
		reSetKey(ws) {
			// 将 Excel 读取的中文 Key 映射为后端接收的英文 Key，无需学校名称
			let temp = []
			ws.forEach((item) => {
				const studentIdRaw = item['学生ID（选填）'] != null && item['学生ID（选填）'] !== ''
					? item['学生ID（选填）']
					: item['学生ID']
				temp.push({
					ExamName: item['考试名称'] || '',
					ExamRoomName: item['考场名称'] || '',
					ClassName: item['班级名称'] || '',
					StudentId: this.parseImportStudentId(studentIdRaw),
					StudentName: item['学生姓名'] || '',
					SeatNumber: item['座位号'] ? Number(item['座位号']) : null
				})
			})
			return temp
		},
		parseImportStudentId(rawId) {
			if (rawId == null || rawId === '') return 0
			if (typeof rawId === 'number') return Math.trunc(rawId)
			const text = String(rawId).trim()
			if (!text) return 0
			const num = Number(text)
			return Number.isFinite(num) ? num : 0
		},

		// --- 座位排布大屏显示 ---
		async showExamStudent(examRow) {
			const res = await getArrangedStudentsByExamId({ examId: examRow.Id });
			if (res.data && res.data.success) {
				this.arrangedRooms = res.data.response || [];
				if (this.arrangedRooms.length > 0) {
					this.selectedRoomId = this.arrangedRooms[0].ExamRoomId;
					this.handleRoomChange();
				} else {
					this.selectedRoomId = '';
					this.arrangedStudents = [];
					this.roomInfo = {};
				}
				this.examStudentDialogTitle = `${examRow.ExamName} - 可视化考场考生监看`;
				this.examStudentDialogVisible = true;
			} else {
				this.$message.error(res.data && res.data.msg ? res.data.msg : '获取考场座位信息失败');
			}
		},
		handleRoomChange() {
			const room = this.arrangedRooms.find(r => r.ExamRoomId === this.selectedRoomId);
			if (room) {
				this.arrangedStudents = room.Students || [];
				this.roomInfo = room;
			} else {
				this.arrangedStudents = [];
				this.roomInfo = {};
			}
		},

		// --- 场次签到记录 ---
		showSessionSignIn(sessionRow) {
			this.currentSignInSession = sessionRow
			this.signInDialogTitle = `签到记录 - ${sessionRow.SubjectName || ''}`
			this.signInFilterRoomId = ''
			this.signInNameKeyword = ''
			this.signInStatusFilter = 'all'
			this.signInPage = 1
			this.signInDialogVisible = true
			this.loadSignInRoomOptions()
			this.loadSignInList()
		},
		loadSignInRoomOptions() {
			if (!this.currentExam || !this.currentExam.Id) {
				this.signInRoomOptions = []
				return
			}
			getExamSessionRooms({ examId: this.currentExam.Id }).then((res) => {
				if (res.data.success && res.data.response) {
					this.signInRoomOptions = res.data.response.map(r => ({
						ExamRoomId: r.ExamRoomId,
						RoomName: r.RoomName
					}))
				} else {
					this.signInRoomOptions = []
				}
			}).catch(() => {
				this.signInRoomOptions = []
			})
		},
		loadSignInList() {
			if (!this.currentExam || !this.currentSignInSession) return
			this.signInListLoading = true
			const para = {
				examId: this.currentExam.Id,
				examSessionId: this.currentSignInSession.Id,
				examRoomId: this.signInFilterRoomId || 0
			}
			getExamSignInList(para).then((res) => {
				this.signInListLoading = false
				if (res.data.success && res.data.response) {
					this.signInStats = {
						Total: res.data.response.Total,
						Signed: res.data.response.Signed,
						Unsigned: res.data.response.Unsigned,
						ManualCount: res.data.response.ManualCount
					}
					this.signInList = res.data.response.Items || []
					this.resetSignInPageIfNeeded()
				} else {
					this.signInStats = null
					this.signInList = []
				}
			}).catch(() => {
				this.signInListLoading = false
				this.signInList = []
			})
		},
		/** 切换签到状态筛选（仅过滤列表，顶部数量不变） */
		setSignInStatusFilter(status) {
			this.signInStatusFilter = status
			this.signInPage = 1
		},
		/** 考场筛选变化时重新拉取，并重置页码 */
		handleSignInRoomFilterChange() {
			this.signInPage = 1
			this.loadSignInList()
		},
		/** 姓名搜索后重置到第一页 */
		handleSignInNameFilterChange() {
			this.signInPage = 1
		},
		handleSignInPageChange(page) {
			this.signInPage = page
		},
		/** 筛选结果变少时，纠正当前页码 */
		resetSignInPageIfNeeded() {
			const maxPage = Math.max(1, Math.ceil(this.filteredSignInList.length / this.signInPageSize) || 1)
			if (this.signInPage > maxPage) {
				this.signInPage = maxPage
			}
		},
		formatSignSource(row) {
			if (row.IsRevoked) return '-'
			if (row.IsManual) return '人工补录'
			if (row.SignSource === 'card') return '刷卡'
			if (row.SignSource === 'face') return '刷脸'
			return row.SignSource || '-'
		},
		handleManualSignIn(row) {
			if (!this.canManageSessionSignIn(this.currentSignInSession)) {
				this.$message.warning(this.getSignInBlockedMessage(this.currentSignInSession))
				return
			}
			this.$confirm(`确认为 ${row.StuName} 补录本场次签到？`, '补录签到', {
				confirmButtonText: '确定',
				cancelButtonText: '取消',
				type: 'warning'
			}).then(() => {
				manualExamSignIn({
					examId: this.currentExam.Id,
					examSessionId: this.currentSignInSession.Id,
					examRoomId: row.ExamRoomId,
					studentId: row.StudentId,
					manualBy: this.getOperatorName()
				}).then((res) => {
					if (res.data.success) {
						this.$message.success(res.data.msg || '补录成功')
						this.loadSignInList()
					} else {
						this.$message.error(res.data.msg || '补录失败')
					}
				})
			}).catch(() => {})
		},
		handleRevokeSignIn(row) {
			if (!this.canManageSessionSignIn(this.currentSignInSession)) {
				this.$message.warning(this.getSignInBlockedMessage(this.currentSignInSession))
				return
			}
			this.$confirm(`确认撤销 ${row.StuName} 的签到记录？`, '撤销签到', {
				confirmButtonText: '确定',
				cancelButtonText: '取消',
				type: 'warning'
			}).then(() => {
				revokeExamSignIn({
					signInId: row.SignInId,
					revokedBy: this.getOperatorName()
				}).then((res) => {
					if (res.data.success) {
						this.$message.success(res.data.msg || '撤销成功')
						this.loadSignInList()
					} else {
						this.$message.error(res.data.msg || '撤销失败')
					}
				})
			}).catch(() => {})
		},
		exportSignInExcel() {
			if (!this.currentExam || !this.currentSignInSession) return
			this.signInExportLoading = true
			exportExamSignIn({
				examId: this.currentExam.Id,
				examSessionId: this.currentSignInSession.Id,
				examRoomId: this.signInFilterRoomId || 0
			}).then((res) => {
				this.signInExportLoading = false
				const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
				const url = URL.createObjectURL(blob)
				const link = document.createElement('a')
				link.href = url
				link.download = `考试签到_${this.currentSignInSession.SubjectName || ''}_${Date.now()}.xlsx`
				link.click()
				URL.revokeObjectURL(url)
			}).catch(() => {
				this.signInExportLoading = false
				this.$message.error('导出失败')
			})
		},

		// --- 打印座位表 ---
		openPrintSeatTable(roomRow) {
			if (!this.currentExamSession || !this.currentExamSession.ExamId) {
				this.$message.warning('请先进入考场编排')
				return
			}
			this.printSeatRoom = roomRow
			getArrangedStudentsByExamId({ examId: this.currentExamSession.ExamId }).then((res) => {
				if (res.data.success && res.data.response) {
					const room = res.data.response.find(r => r.ExamRoomId === roomRow.ExamRoomId)
					this.printSeatStudents = room && room.Students ? room.Students : []
					this.printSeatDialogVisible = true
				} else {
					this.$message.error(res.data.msg || '获取座位数据失败')
				}
			}).catch(() => {
				this.$message.error('获取座位数据失败')
			})
		},
		doPrintSeatTable() {
			const printArea = this.$refs.printSeatArea
			if (!printArea) return
			const printWindow = window.open('', '_blank')
			if (!printWindow) {
				this.$message.warning('请允许弹出窗口以完成打印')
				return
			}
			const styles = `
				<style>
					body { font-family: "Microsoft YaHei", sans-serif; padding: 16px; }
					h2 { text-align: center; margin-bottom: 8px; font-size: 18px; }
					.print-seat-meta { text-align: center; margin-bottom: 16px; color: #333; font-size: 14px; }
					.print-seat-grid { display: grid; gap: 6px; }
					.print-seat-cell { border: 1px solid #333; padding: 6px; min-height: 52px; font-size: 12px; text-align: center; }
					.print-seat-no { font-weight: bold; font-size: 13px; }
					.print-seat-name { margin-top: 4px; }
					.print-seat-code { font-size: 11px; color: #555; }
					.empty-seat { color: #aaa; margin-top: 8px; }
					@media print { body { padding: 0; } }
				</style>
			`
			printWindow.document.write(`<html><head><title>座位表</title>${styles}</head><body>${printArea.innerHTML}</body></html>`)
			printWindow.document.close()
			printWindow.focus()
			setTimeout(() => {
				printWindow.print()
				printWindow.close()
			}, 300)
		},
	},
	mounted() {
		let routers = window.localStorage.router
			? JSON.parse(window.localStorage.router)
			: []
		this.buttonList = getButtonList(this.$route.path, routers)
	},
}
</script>

<style scoped>
/* 业务布局；滚动由全局 dp-layout/dp-right 统一处理 */
.exam-management-layout .dp-right {
	min-width: 0;
}

.statistics {
	display: flex;
	align-items: center;
	gap: 20px;
	margin-bottom: 8px;
}

.statistics span {
	font-size: 14px;
	color: #606266;
}

.modern-table {
	width: 100%;
	border: none !important;
}

.modern-table ::v-deep th.el-table__cell {
	background-color: #f8fafc !important;
	color: #475569 !important;
	font-weight: 600;
	height: 48px;
	border-bottom: 2px solid #e2e8f0;
}

.modern-table ::v-deep td.el-table__cell {
	border-bottom: 1px solid #f1f5f9;
	padding: 10px 0;
	color: #334155;
}

.modern-table ::v-deep .el-table__row:hover > td.el-table__cell {
	background-color: #f1f7ff !important;
}

.exam-name-text {
	font-weight: 700;
	color: #1e293b;
	font-size: 14px;
}

/* 时间段展示美化 */
.time-range-display {
	display: flex;
	flex-direction: column;
	font-size: 12px;
	color: #475569;
	gap: 2px;
}

.time-range-display.aligned-left {
	text-align: left;
}

.time-start {
	font-weight: 600;
}

.time-end {
	color: #64748b;
}

.time-sep {
	font-size: 10px;
	color: #94a3b8;
	line-height: 1;
	padding-left: 2px;
}

.session-badge {
	background: rgba(115, 103, 240, 0.1);
	color: #7367f0;
	padding: 4px 8px;
	border-radius: 6px;
	font-weight: 600;
	font-size: 13px;
}

/* 表格内微型统计网格 */
.mini-stats-grid {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 6px;
	padding: 4px;
	background: #f8fafc;
	border-radius: 8px;
	border: 1px solid #e2e8f0;
}

.mini-stat-item {
	display: flex;
	flex-direction: column;
	align-items: center;
	padding: 4px 6px;
	background: #ffffff;
	border-radius: 4px;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
}

.mini-stat-label {
	font-size: 9px;
	color: #64748b;
	transform: scale(0.9);
}

.mini-stat-val {
	font-size: 12px;
	font-weight: 700;
	color: #334155;
	margin-top: 1px;
}

.mini-stat-item.highlight .mini-stat-val {
	color: #67c23a;
}

.mini-stat-item.warning .mini-stat-val {
	color: #e6a23c;
}

/* 按钮组与按钮定制 */
.btn-group-row {
	display: flex;
	justify-content: center;
	gap: 8px;
}

.divider {
	color: #cbd5e1;
	font-size: 12px;
	line-height: 28px;
}

.modern-table .el-button--text {
	padding: 4px 8px;
	font-weight: 600;
	border: none;
	box-shadow: none;
	border-radius: 4px;
	transition: all 0.2s ease;
}

.modern-table .el-button--text:focus,
.modern-table .el-button--text:active {
	border: none;
	box-shadow: none;
	outline: none;
}

.btn-edit { color: var(--btn-primary, #2563eb); }
.btn-edit:hover { background: rgba(64, 158, 255, 0.1); }
.btn-delete { color: #f56c6c; }
.btn-delete:hover { background: rgba(245, 108, 108, 0.1); }
.btn-force-delete { color: #dc2626; font-weight: 600; }
.btn-force-delete:hover { background: rgba(220, 38, 38, 0.08); }

.force-delete-target {
	padding: 10px 12px;
	background: #fef2f2;
	border: 1px solid #fecaca;
	border-radius: 8px;
	font-size: 14px;
	color: #991b1b;
}
.force-delete-label {
	color: #b91c1c;
	margin-right: 4px;
}

::v-deep .force-delete-auth-input.custom-mask-input .el-input__inner {
	-webkit-text-security: disc !important;
}

.force-delete-eye-icon {
	cursor: pointer;
	margin-right: 6px;
	color: #9ca3af;
}

.btn-view { color: #7367f0; }
.btn-view:hover { background: rgba(115, 103, 240, 0.1); }
.btn-seat { color: #ff9f43; }
.btn-seat:hover { background: rgba(255, 159, 67, 0.1); }

.tag-group-wrapper {
	display: flex;
	flex-wrap: wrap;
	gap: 4px;
	align-items: center;
}

.custom-tag {
	border-radius: 4px;
	font-weight: 500;
}

.btn-more-tags {
	padding: 2px 4px !important;
	font-size: 11px !important;
	color: #64748b !important;
}

.pagination-wrapper {
	padding: 16px;
	display: flex;
	justify-content: flex-end;
}

/* 场次管理盒子风格 */
.session-management-box {
	display: flex;
	flex-direction: column;
	gap: 16px;
}

.box-header-row {
	display: flex;
	justify-content: space-between;
	align-items: center;
	background: #f1f5f9;
	padding: 12px 18px;
	border-radius: 10px;
	border: 1px solid #e2e8f0;
}

.exam-title-badge {
	font-weight: 700;
	color: #334155;
	display: flex;
	align-items: center;
	gap: 8px;
}

.exam-title-badge .dot {
	width: 8px;
	height: 8px;
	border-radius: 50%;
	background: var(--btn-primary, #2563eb);
}

.shadow-inner-table {
	border: 1px solid #edf2f7;
	border-radius: 8px;
	overflow: hidden;
}

.subject-bold-text {
	font-weight: 700;
	color: #0f172a;
}

/* 弹窗主体内容区全局样式：允许横向滚动以适配中窄屏 */
::v-deep .width-large .el-dialog__body {
	overflow-x: auto;
	-webkit-overflow-scrolling: touch;
}

/* 限制大屏下监考人员分配弹窗的最大宽度，防止过度拉伸占满空间，显得更窄精致 */
::v-deep .glass-modal.modal-invigilator {
	max-width: 1080px;
}

/* 双栏工作空间穿梭组件设计 */
.workspace-shuttle-container {
	display: grid;
	grid-template-columns: minmax(0, 1fr) 100px minmax(0, 1fr); /* 均等分配两栏宽度，使中间按钮保持水平居中 */
	gap: 16px;
	align-items: start;
	min-width: 960px; /* 全局设定最小宽度，防止小屏幕下内容被无限挤压，并触发横滑 */
}

.workspace-section {
	background: #ffffff;
	border-radius: 12px;
	border: 1px solid #edf2f7;
	box-shadow: 0 4px 15px rgba(0, 0, 0, 0.03);
	padding: 16px;
	display: flex;
	flex-direction: column;
	gap: 14px;
}

.section-title-badge {
	font-size: 14px;
	font-weight: 700;
	padding: 6px 14px;
	border-radius: 8px;
	align-self: flex-start;
}

.color-blue {
	background: rgba(64, 158, 255, 0.1);
	color: var(--btn-primary, #2563eb);
}

.color-green {
	background: rgba(103, 194, 58, 0.1);
	color: #67c23a;
}

.section-table-box {
	border: 1px solid #f1f5f9;
	border-radius: 8px;
	overflow: hidden;
}

.no-border ::v-deep th.el-table__cell,
.no-border ::v-deep td.el-table__cell {
	padding: 8px 0;
}

/* 穿梭框中间转移按钮 */
.workspace-action-middle {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	align-self: center; /* 确保在穿梭容器中纵向居中 */
}

.shuttle-action-button {
	width: 68px;
	height: 68px;
	border-radius: 50%;
	background: var(--btn-primary, #2563eb);
	color: #ffffff;
	border: none;
	cursor: pointer;
	box-shadow: 0 6px 20px rgba(64, 158, 255, 0.35);
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 4px;
	transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
}

.shuttle-action-button:hover {
	transform: scale(1.05);
	background: #338ee6;
	box-shadow: 0 8px 25px rgba(64, 158, 255, 0.45);
}

.shuttle-action-button:active {
	transform: scale(0.96);
}

.shuttle-action-button:disabled {
	background: #cbd5e1;
	color: #94a3b8;
	cursor: not-allowed;
	box-shadow: none;
}

.shuttle-action-button svg {
	transition: transform 0.2s ease;
}

.shuttle-action-button:hover svg {
	transform: translateX(-3px);
}

.shuttle-btn-text {
	font-size: 11px;
	font-weight: 700;
	letter-spacing: 0.5px;
}

.room-assigned-name {
	font-weight: 700;
	color: #1e293b;
}

.btn-delete-action {
	color: #f56c6c !important;
}

.btn-delete-action:hover {
	background: rgba(245, 108, 108, 0.1);
}

.teacher-bold-name, .student-bold-name {
	font-weight: 700;
	color: #0f172a;
}

.seat-badge-no {
	background: #f1f5f9;
	color: #475569;
	padding: 2px 6px;
	border-radius: 4px;
	font-size: 11px;
	font-weight: 600;
}

.table-footer-pagination {
	padding: 8px;
	background: #f8fafc;
	border-top: 1px solid #f1f5f9;
	display: flex;
	justify-content: flex-end;
}

/* 标签列表布局 */
.all-tags-layout {
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
	max-height: 350px;
	overflow-y: auto;
	padding: 8px;
}

.dialog-tag-pill {
	border-radius: 6px;
	font-weight: 500;
	padding: 4px 10px;
}

.empty-placeholder {
	width: 100%;
	text-align: center;
	color: #94a3b8;
	padding: 30px 0;
	font-size: 13px;
}

/* 可视化座位大屏设计 */
.seat-visual-header-card {
	background: #ffffff;
	border-radius: 12px;
	border: 1px solid #edf2f7;
	padding: 16px 24px;
	display: flex;
	justify-content: space-between;
	align-items: center;
	flex-wrap: wrap;
	gap: 16px;
	box-shadow: 0 2px 10px rgba(0, 0, 0, 0.02);
	margin-bottom: 20px;
}

.room-selector-section {
	display: flex;
	align-items: center;
}

.room-selector-section .label {
	font-weight: 700;
	color: #475569;
}

.room-meta-dashboard {
	display: flex;
	gap: 24px;
}

.meta-item {
	display: flex;
	flex-direction: column;
	border-left: 3px solid #cbd5e1;
	padding-left: 12px;
}

.meta-item:first-child {
	border-left-color: #7367f0;
}

.meta-item:nth-child(2) {
	border-left-color: #ff9f43;
}

.meta-item:nth-child(3) {
	border-left-color: #67c23a;
}

.meta-label {
	font-size: 11px;
	color: #64748b;
	text-transform: uppercase;
}

.meta-value {
	font-size: 15px;
	font-weight: 700;
	color: #1e293b;
	margin-top: 2px;
}

.meta-value.success { color: #67c23a; }
.meta-value.highlight { color: #ff9f43; }

/* 拟物化课桌 Grid 排布区 */
.seat-map-scroll-area {
	background: #f1f5f9;
	border-radius: 12px;
	border: 1px solid #e2e8f0;
	padding: 24px;
	min-height: 400px;
	max-height: 550px;
	overflow-y: auto;
	box-shadow: inset 0 2px 8px rgba(0, 0, 0, 0.04);
}

.desk-grid-layout {
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
	gap: 20px;
}

.desk-card {
	background: #ffffff;
	border-radius: 10px;
	border: 1px solid #e2e8f0;
	box-shadow: 0 4px 10px rgba(0, 0, 0, 0.04);
	display: flex;
	flex-direction: column;
	overflow: hidden;
	transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
}

.desk-card:hover {
	transform: translateY(-4px) scale(1.02);
	box-shadow: 0 10px 25px rgba(64, 158, 255, 0.18);
	border-color: var(--btn-primary, #2563eb);
}

.desk-header {
	background: #f8fafc;
	padding: 6px 12px;
	border-bottom: 1px solid #e2e8f0;
	display: flex;
	align-items: center;
	gap: 6px;
}

.desk-no {
	font-size: 16px;
	font-weight: 800;
	color: var(--btn-primary, #2563eb);
}

.desk-label {
	font-size: 10px;
	color: #94a3b8;
	font-weight: 700;
	text-transform: uppercase;
}

.desk-body {
	padding: 12px;
	display: flex;
	gap: 10px;
	align-items: center;
}

.student-avatar-box {
	width: 38px;
	height: 38px;
	border-radius: 50%;
	background: rgba(64, 158, 255, 0.08);
	color: var(--btn-primary, #2563eb);
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
}

.student-details {
	display: flex;
	flex-direction: column;
	min-width: 0;
}

.student-name {
	font-weight: 700;
	color: #1e293b;
	font-size: 14px;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.student-code {
	font-size: 11px;
	color: #64748b;
	margin-top: 2px;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.student-class {
	font-size: 11px;
	color: #94a3b8;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.desk-footer {
	background: rgba(103, 194, 58, 0.08);
	padding: 4px 12px;
	border-top: 1px dashed rgba(103, 194, 58, 0.2);
	display: flex;
	justify-content: flex-end;
}

.desk-footer-status {
	font-size: 9px;
	color: #67c23a;
	font-weight: 700;
}

/* 拟物化空座位大屏 */
.seat-empty-view {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	min-height: 350px;
	color: #94a3b8;
}

.empty-icon-box {
	margin-bottom: 12px;
	color: #cbd5e1;
}

.empty-text {
	font-size: 14px;
	font-weight: 600;
}

.btn-remove-import-row {
	color: #f56c6c;
	cursor: pointer;
	font-weight: 600;
}

.btn-remove-import-row:hover {
	text-decoration: underline;
}

/* 导入向导专有样式 */
.import-steps-indicator {
	margin-bottom: 28px;
	padding: 10px 0;
}

.import-step-one {
	display: grid;
	grid-template-columns: 1fr 1.2fr;
	gap: 20px;
	min-height: 220px;
	align-items: stretch;
}

.template-download-card {
	background: #ffffff;
	border-radius: 12px;
	border: 2px dashed #cbd5e1;
	padding: 24px;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	text-align: center;
	cursor: pointer;
	transition: all 0.2s ease;
}

.template-download-card:hover {
	border-color: #7367f0;
	background: rgba(115, 103, 240, 0.02);
	transform: translateY(-2px);
}

.template-download-card .card-icon {
	color: #7367f0;
	margin-bottom: 12px;
	background: rgba(115, 103, 240, 0.08);
	width: 60px;
	height: 60px;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
}

.template-download-card h4 {
	font-size: 15px;
	color: #1e293b;
	margin: 0 0 6px;
	font-weight: 700;
}

.template-download-card p {
	font-size: 11px;
	color: #64748b;
	margin: 0 0 16px;
	line-height: 1.4;
}

.import-template-tip {
	font-size: 11px;
	color: #b45309;
	margin: -10px 0 16px;
	line-height: 1.5;
}

.btn-download-text {
	font-weight: 700;
	color: #7367f0 !important;
}

.file-upload-drag-area {
	background: #ffffff;
	border-radius: 12px;
	border: 2px dashed var(--btn-primary, #2563eb);
	padding: 24px;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	text-align: center;
	cursor: pointer;
	transition: all 0.2s ease;
}

.file-upload-drag-area:hover {
	background: rgba(64, 158, 255, 0.02);
	transform: translateY(-2px);
}

.file-upload-drag-area .upload-icon {
	color: var(--btn-primary, #2563eb);
	margin-bottom: 12px;
	background: rgba(64, 158, 255, 0.08);
	width: 68px;
	height: 68px;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	transition: transform 0.25s ease;
}

.file-upload-drag-area:hover .upload-icon {
	transform: translateY(-4px);
}

.file-upload-drag-area h4 {
	font-size: 15px;
	color: #1e293b;
	margin: 0 0 6px;
	font-weight: 700;
}

.file-upload-drag-area p {
	font-size: 12px;
	color: #64748b;
	margin: 0;
}

.file-upload-drag-area .highlight {
	color: var(--btn-primary, #2563eb);
	font-weight: 700;
	text-decoration: underline;
}

.preview-stats-row {
	margin-bottom: 12px;
	font-size: 13px;
	color: #475569;
}

.preview-stats-row strong {
	color: var(--btn-primary, #2563eb);
	font-size: 15px;
}

.import-step-three {
	display: flex;
	flex-direction: column;
	gap: 20px;
}

.result-indicator-box {
	border-radius: 12px;
	padding: 20px;
	display: flex;
	align-items: center;
	gap: 24px;
	border-width: 1px;
	border-style: solid;
}

.result-indicator-box.all-success {
	background: rgba(103, 194, 58, 0.06);
	border-color: rgba(103, 194, 58, 0.2);
}

.result-indicator-box.part-fail {
	background: rgba(230, 162, 44, 0.06);
	border-color: rgba(230, 162, 44, 0.25);
}

.result-radial-summary {
	display: flex;
	gap: 16px;
}

.summary-metric {
	display: flex;
	flex-direction: column;
	align-items: center;
	background: #ffffff;
	padding: 10px 16px;
	border-radius: 8px;
	border: 1px solid rgba(0, 0, 0, 0.05);
	min-width: 80px;
}

.summary-metric .metric-val {
	font-size: 20px;
	font-weight: 800;
	color: #67c23a;
}

.summary-metric.warning .metric-val {
	color: #e6a23c;
}

.summary-metric .metric-label {
	font-size: 10px;
	color: #64748b;
	margin-top: 4px;
}

.result-message-alert {
	flex: 1;
}

.result-message-alert h4 {
	margin: 0 0 4px;
	font-size: 16px;
	font-weight: 700;
}

.result-message-alert p {
	margin: 0;
	font-size: 12px;
	color: #475569;
}

.error-details-section h4 {
	font-size: 14px;
	font-weight: 700;
	color: #334155;
	margin: 0 0 10px;
}

.error-logs-list {
	max-height: 200px;
	overflow-y: auto;
	background: #f8fafc;
	border-radius: 8px;
	border: 1px solid #e2e8f0;
	padding: 12px;
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.error-log-item {
	display: flex;
	align-items: flex-start;
	gap: 10px;
	font-size: 12px;
	line-height: 1.5;
}

.error-log-item .item-index {
	background: rgba(245, 108, 108, 0.1);
	color: #f56c6c;
	font-size: 10px;
	font-weight: 700;
	padding: 1px 6px;
	border-radius: 4px;
	margin-top: 1px;
}

.error-log-item .item-text {
	color: #475569;
}

/* ===== 监考老师卡片行样式 ===== */
.invig-table ::v-deep .el-table__row {
	height: 58px;
}

.invig-card {
	display: flex;
	align-items: center;
	gap: 10px;
	padding: 2px 0;
}

.invig-avatar {
	width: 40px;
	height: 40px;
	border-radius: 50%;
	overflow: hidden;
	flex-shrink: 0;
	border: 2px solid #e2e8f0;
	box-shadow: 0 2px 8px rgba(0,0,0,0.08);
	background: #f1f5f9;
}

.invig-avatar .el-image {
	width: 100%;
	height: 100%;
}

.invig-avatar-fallback {
	width: 100%;
	height: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
	color: #94a3b8;
	font-size: 18px;
	background: linear-gradient(135deg, #e0e7ff 0%, #f0f9ff 100%);
}

.invig-info {
	display: flex;
	flex-direction: column;
	gap: 2px;
	min-width: 0;
}

.invig-name {
	font-size: 14px;
	font-weight: 600;
	color: #1e293b;
	line-height: 1.3;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.invig-dept {
	font-size: 12px;
	color: #64748b;
	line-height: 1.2;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.invig-card-no {
	font-size: 13px;
	color: #475569;
	font-family: 'Courier New', monospace;
	letter-spacing: 0.5px;
}

/* ===== 监考老师多列 Grid 卡片 ===== */
.invig-grid-box {
	height: 420px;
	overflow-y: auto;
	padding: 8px 4px 8px 8px;
}

.invig-grid {
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(130px, 1fr)); /* 使用 auto-fill 自适应列，避免老师较少时卡片被强行横向拉宽 */
	gap: 10px;
	align-content: start;
}

/* 卡片基础 */
.invig-tile {
	position: relative;
	background: #fff;
	border: 1.5px solid #e8edf5;
	border-radius: 12px;
	padding: 10px 8px 8px; /* 缩减内边距，使卡片更紧凑 */
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 4px; /* 缩减子元素间距 */
	text-align: center;
	transition: box-shadow 0.2s, border-color 0.2s, transform 0.15s;
	box-shadow: 0 1px 4px rgba(0,0,0,0.05);
}

.invig-tile:hover {
	box-shadow: 0 4px 14px rgba(64,158,255,0.12);
}

/* 可选卡片 */
.invig-tile-selectable {
	cursor: pointer;
}

.invig-tile-selectable:hover {
	border-color: #a8c8ff;
	transform: translateY(-1px);
}

.invig-tile.is-selected {
	border-color: var(--btn-primary, #2563eb);
	background: linear-gradient(135deg, #f0f7ff 0%, #fff 100%);
	box-shadow: 0 4px 14px rgba(64,158,255,0.2);
}

/* 勾选徽章 */
.invig-check-badge {
	position: absolute;
	top: 6px;
	right: 6px;
	width: 20px;
	height: 20px;
	border-radius: 50%;
	background: var(--btn-primary, #2563eb);
	color: #fff;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 12px;
	font-weight: bold;
}

/* 移除按钮 */
.invig-remove-btn {
	position: absolute;
	top: 6px;
	right: 6px;
	width: 20px;
	height: 20px;
	border: none;
	border-radius: 50%;
	background: rgba(239,68,68,0.08);
	color: #ef4444;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 11px;
	cursor: pointer;
	padding: 0;
	line-height: 1;
	transition: background 0.2s, color 0.2s;
}

.invig-remove-btn:hover {
	background: #ef4444;
	color: #fff;
}

/* 头像 */
.invig-tile-avatar {
	width: 44px; /* 头像缩小为 44px */
	height: 44px;
	border-radius: 50%;
	overflow: hidden;
	border: 1.5px solid #e2e8f0; /* 边框变细 */
	box-shadow: 0 2px 8px rgba(0,0,0,0.08);
	flex-shrink: 0;
}

.invig-tile-avatar .el-image {
	width: 100%;
	height: 100%;
}

.invig-tile-avatar-fb {
	width: 100%;
	height: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
	background: linear-gradient(135deg, #e0e7ff 0%, #f0f9ff 100%);
	color: #94a3b8;
	font-size: 18px; /* 默认图标大小调整 */
}

/* 文字 */
.invig-tile-name {
	font-size: 13px; /* 字体改小为 13px */
	font-weight: 700;
	color: #1e293b;
	line-height: 1.3;
	margin-top: 2px;
}

.invig-tile-dept {
	font-size: 11px;
	color: #64748b;
	max-width: 100%;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.invig-tile-card {
	font-size: 11px;
	color: var(--btn-primary, #2563eb);
	background: #f0f7ff;
	border-radius: 6px;
	padding: 2px 8px;
	font-family: 'Courier New', monospace;
	letter-spacing: 0.3px;
}

/* 空状态 */
.invig-empty {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	height: 200px;
	color: #cbd5e1;
	gap: 8px;
	font-size: 13px;
}

/* 已选人数提示 */
.invig-selected-hint {
	margin-left: 8px;
	font-size: 12px;
	color: var(--btn-primary, #2563eb);
	font-weight: 600;
	background: #e8f4ff;
	border-radius: 8px;
	padding: 1px 8px;
}

/* ===== 右侧可用老师搜索框 ===== */
.invig-search-box {
	padding: 8px 10px 6px;
}

.invig-search-box .el-input__inner {
	border-radius: 20px;
	background: #f8fafd;
	border-color: #e2e8f0;
	font-size: 13px;
	transition: border-color 0.2s, box-shadow 0.2s;
}

.invig-search-box .el-input__inner:focus {
	border-color: var(--btn-primary, #2563eb);
	box-shadow: 0 0 0 3px rgba(64,158,255,0.12);
	background: #fff;
}

.invig-search-box .el-input__prefix {
	color: #94a3b8;
}

/* ========================================
   响应式布局 — 弹窗穿梭框适配小屏幕
   ======================================== */

/* 中等屏幕：弹窗扩宽，grid-box 降高 */
@media (max-width: 1100px) {
	::v-deep .glass-modal.width-large {
		width: 92% !important;
		margin: 0 auto;
	}

	/* 穿梭框在中等屏幕下中间列缩窄 */
	.workspace-shuttle-container {
		grid-template-columns: minmax(0, 1fr) 72px minmax(0, 1fr);
		gap: 10px;
		min-width: 880px; /* 中等屏幕下最小宽度限制在 880px */
	}

	.shuttle-action-button {
		width: 52px;
		height: 52px;
	}

	.invig-grid-box,
	.section-table-box {
		max-height: 340px;
	}
}

/* 小屏幕：调整弹窗宽度和最小高度 */
@media (max-width: 768px) {
	::v-deep .glass-modal.width-large {
		width: 95% !important;
		max-height: 90vh;
	}

	/* 弹窗 body 内边距缩小 */
	::v-deep .width-large .el-dialog__body {
		padding: 14px;
	}

	/* 穿梭容器设定最小宽度以触发滚动，避免内容被过分压缩 */
	.workspace-shuttle-container {
		grid-template-columns: minmax(0, 1fr) 72px minmax(0, 1fr);
		gap: 12px;
		min-width: 800px; /* 小屏幕下最小宽度设定为 800px 以保证等宽对称和横向滑动 */
	}

	/* 中间按钮区域调整 */
	.shuttle-action-button {
		width: 48px;
		height: 48px;
	}

	/* 降低卡片区域和表格区域的高度，使其更适合小屏纵向展示 */
	.invig-grid-box,
	.section-table-box {
		max-height: 280px;
	}
}

/* 超小屏幕 */
@media (max-width: 480px) {
	::v-deep .glass-modal.width-large {
		width: 98% !important;
	}

	.workspace-shuttle-container {
		min-width: 720px; /* 超小屏幕同样设定最小宽度以保证可读性 */
	}
}

/* 自定义弹窗标题头部样式（带提示信息） */
.custom-dialog-header {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 16px;
	padding-right: 24px;
	/* 指定现代无衬线中英文字体集以替换粗糙的默认字体 */
	font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Microsoft YaHei", sans-serif;
}

.custom-dialog-header .main-title {
	font-size: 18px;
	font-weight: 700;
	color: #0f172a;
}

/* 升级为饱满、富有质感的淡蓝色主题胶囊提示框 */
.custom-dialog-header .header-sub-info {
	display: flex;
	align-items: center;
	gap: 6px;
	background: rgba(64, 158, 255, 0.07);
	border: 1px solid rgba(64, 158, 255, 0.18);
	padding: 5px 14px;
	border-radius: 30px;
	margin-left: 4px;
}

.custom-dialog-header .icon-hint {
	color: var(--btn-primary, #2563eb);
	font-size: 15px;
}

.custom-dialog-header .hint-text {
	font-size: 12px;
	color: #2563eb;
	font-weight: 600;
	letter-spacing: 0.4px;
	line-height: 1.2;
}



/* 学生头像及表格内头像样式系统 */
.table-avatar-cell {
	display: flex;
	align-items: center;
	gap: 10px;
}

.table-student-avatar {
	width: 26px;
	height: 26px;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 10px;
	font-weight: 700;
	flex-shrink: 0;
	box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.06);
	user-select: none;
}

</style>

<style>
/* 全局样式：解决二次弹窗 append-to-body 导致局部 scoped 样式失效的问题 */
.glass-modal.el-dialog {
	margin-top: 4vh !important;
}

.glass-modal.seat-preview-dialog.el-dialog {
	max-width: 1120px;
	min-width: 640px;
}

.glass-modal.student-assignment-dialog.el-dialog {
	max-width: 1280px;
	min-width: 720px;
}

/* 考生分配弹窗：穿梭区随弹窗自适应，避免固定列错位与多余横滚 */
.student-assignment-dialog .student-assign-hint {
	display: flex;
	align-items: flex-start;
	gap: 8px;
	padding: 8px 12px;
	margin-bottom: 12px;
	border-radius: 8px;
	background: #f0f9ff;
	border: 1px solid #dbeafe;
	color: #0369a1;
	font-size: 12px;
	line-height: 1.5;
}

.student-assignment-dialog .student-assign-hint .el-icon-info {
	margin-top: 2px;
	flex-shrink: 0;
}

.student-assignment-dialog .student-assign-dialog-title {
	display: block;
}

.student-assignment-dialog .student-assign-dialog-title .main-title {
	font-size: 17px;
}

.student-assignment-dialog .student-assign-shuttle.workspace-shuttle-container {
	min-width: 0;
	grid-template-columns: minmax(0, 1fr) 72px minmax(0, 1fr);
	gap: 12px;
	align-items: stretch;
}

.student-assignment-dialog .student-assigned-panel,
.student-assignment-dialog .student-pending-panel {
	padding: 14px;
}

.student-assignment-dialog .student-table-card {
	display: flex;
	flex-direction: column;
	gap: 10px;
	height: 100%;
}

.student-assignment-dialog .student-table-toolbar {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	min-height: 32px;
}

.student-assignment-dialog .student-table-toolbar-left {
	display: flex;
	align-items: center;
	gap: 8px;
	flex-wrap: wrap;
	min-width: 0;
}

.student-assignment-dialog .student-selected-hint {
	font-size: 12px;
	font-weight: 600;
	color: #15803d;
	background: #dcfce7;
	border-radius: 10px;
	padding: 2px 10px;
	line-height: 20px;
}

.student-assignment-dialog .student-select-all-btn,
.student-assignment-dialog .student-clear-select-btn {
	padding: 0 4px;
}

.student-assignment-dialog .student-table-toolbar--single {
	min-height: 32px;
}

.student-assignment-dialog .student-table-toolbar-actions {
	display: flex;
	flex-wrap: wrap;
	gap: 6px;
	justify-content: flex-end;
}

.student-assignment-dialog .student-assigned-table-box,
.student-assignment-dialog .student-pending-table-box {
	border: 1px solid #e2e8f0;
	border-radius: 8px;
	overflow: hidden;
	background: #fff;
}

.student-assignment-dialog .stu-grid-table {
	width: 100%;
}

.student-assignment-dialog .stu-grid-head,
.student-assignment-dialog .stu-grid-row {
	display: grid;
	grid-template-columns: 52px minmax(0, 1.65fr) minmax(88px, 0.75fr) 76px 48px;
	align-items: center;
	min-height: 48px;
}

.student-assignment-dialog .stu-grid-head {
	background: #f8fafc;
	border-bottom: 1px solid #e2e8f0;
	color: #475569;
	font-size: 12px;
	font-weight: 600;
	min-height: 40px;
}

.student-assignment-dialog .stu-grid-body {
	overflow-y: auto;
	overflow-x: hidden;
}

.student-assignment-dialog .stu-grid-row {
	border-bottom: 1px solid #f1f5f9;
	transition: background-color 0.15s ease;
}

.student-assignment-dialog .stu-grid-row--clickable {
	cursor: pointer;
}

.student-assignment-dialog .stu-grid-row:last-child {
	border-bottom: none;
}

.student-assignment-dialog .stu-grid-row:hover {
	background: #f8fafc;
}

.student-assignment-dialog .stu-grid-row.is-selected {
	background: #eff6ff;
}

.student-assignment-dialog .stu-grid-cell {
	min-width: 0;
	padding: 0 10px;
	display: flex;
	align-items: center;
}

.student-assignment-dialog .stu-grid-cell--check {
	justify-content: center;
	padding: 0 8px;
}

.student-assignment-dialog .stu-grid-cell--code {
	color: #334155;
	font-size: 13px;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.student-assignment-dialog .stu-grid-cell--seat {
	justify-content: center;
	padding: 0 6px;
}

.student-assignment-dialog .stu-grid-cell--action {
	justify-content: center;
	padding: 0 4px;
}

.student-assignment-dialog .stu-grid-cell--ghost {
	pointer-events: none;
}

.student-assignment-dialog .stu-grid-empty {
	height: 100%;
	min-height: 160px;
	display: flex;
	align-items: center;
	justify-content: center;
	color: #94a3b8;
	font-size: 13px;
}

.student-assignment-dialog .table-footer-pagination {
	padding: 8px 12px;
	border-top: 1px solid #f1f5f9;
	background: #fff;
	display: flex;
	justify-content: flex-end;
}

.student-assignment-dialog .student-cell-compact {
	display: flex;
	align-items: center;
	gap: 10px;
	min-width: 0;
}

.student-assignment-dialog .student-cell-avatar {
	width: 32px;
	height: 32px;
	border-radius: 50%;
	overflow: hidden;
	flex-shrink: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 11px;
	font-weight: 700;
}

.student-assignment-dialog .student-cell-avatar .avatar-img {
	width: 100%;
	height: 100%;
	object-fit: cover;
	display: block;
}

.student-assignment-dialog .student-cell-meta {
	min-width: 0;
	flex: 1;
}

.student-assignment-dialog .student-cell-name {
	font-size: 13px;
	font-weight: 600;
	color: #1e293b;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.student-assignment-dialog .student-cell-sub {
	font-size: 11px;
	color: #94a3b8;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	margin-top: 2px;
}

.student-assignment-dialog .stu-grid-cell--name .student-cell-compact {
	width: 100%;
}

.student-assignment-dialog .seat-inline-input {
	width: 64px;
}

.student-assignment-dialog .seat-inline-input .el-input__inner {
	text-align: center;
	padding: 0 6px;
	height: 28px;
	line-height: 28px;
}

.student-assignment-dialog .btn-remove-icon {
	color: #f56c6c;
	padding: 4px;
	font-size: 16px;
}

.student-assignment-dialog .btn-remove-icon:hover {
	color: #f23c3c;
}

.student-assignment-dialog .shuttle-action-button {
	width: 56px;
	height: 56px;
}

.glass-modal .preview-student-grid {
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	gap: 12px;
	max-height: 380px;
	overflow-y: auto;
	padding: 6px;
	scrollbar-width: thin;
	scrollbar-color: rgba(156, 163, 175, 0.45) transparent;
}

/* 座位图预览编排弹窗 */
.seat-preview-dialog .seat-preview-ops-card {
	background: #f8fafc;
	border: 1px solid #e2e8f0;
	border-radius: 10px;
	padding: 12px 14px;
	margin-bottom: 12px;
}

.seat-preview-dialog .seat-rule-row {
	display: flex;
	align-items: flex-start;
	gap: 12px;
	padding: 6px 0;
}

.seat-preview-dialog .seat-rule-row + .seat-rule-row {
	border-top: 1px dashed #e2e8f0;
	margin-top: 4px;
	padding-top: 10px;
}

.seat-preview-dialog .seat-rule-row-label {
	flex-shrink: 0;
	width: 56px;
	font-size: 12px;
	font-weight: 600;
	color: #64748b;
	line-height: 28px;
}

.seat-preview-dialog .seat-rule-pills {
	flex: 1;
	min-width: 0;
	display: flex;
	flex-wrap: wrap;
	gap: 6px;
}

.seat-preview-dialog .seat-rule-pills--path {
	flex-wrap: nowrap;
}

.seat-preview-dialog .seat-rule-pill-wrap {
	display: inline-flex;
}

.seat-preview-dialog .seat-rule-pills--path .seat-rule-pill-wrap {
	flex: 1;
	min-width: 0;
}

.seat-preview-dialog .seat-rule-pill {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	height: 28px;
	padding: 0 12px;
	font-size: 12px;
	color: #475569;
	background: #fff;
	border: 1px solid #dcdfe6;
	border-radius: 6px;
	cursor: pointer;
	outline: none;
	transition: all 0.15s ease;
	white-space: nowrap;
}

.seat-preview-dialog .seat-rule-pills--path .seat-rule-pill {
	width: 100%;
	min-width: 72px;
}

.seat-preview-dialog .seat-rule-pill:hover {
	color: var(--btn-primary, #2563eb);
	border-color: #b3d8ff;
	background: #ecf5ff;
}

.seat-preview-dialog .seat-rule-pill.is-active {
	color: #fff;
	background: var(--btn-primary, #2563eb);
	border-color: var(--btn-primary, #2563eb);
	box-shadow: 0 1px 4px rgba(64, 158, 255, 0.35);
}

.seat-preview-dialog .seat-preview-legend {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 8px 12px;
	margin-top: 10px;
	padding-top: 10px;
	border-top: 1px solid #e2e8f0;
	font-size: 12px;
	line-height: 1.4;
}

.seat-preview-dialog .legend-chip {
	display: inline-flex;
	align-items: center;
	padding: 2px 8px;
	border-radius: 4px;
	font-size: 11px;
	color: #64748b;
	background: #fff;
	border: 1px solid #e2e8f0;
}

.seat-preview-dialog .legend-chip--locked::before {
	content: '';
	display: inline-block;
	width: 8px;
	height: 8px;
	margin-right: 4px;
	border-radius: 2px;
	background: #cbd5e1;
}

.seat-preview-dialog .legend-chip--new::before {
	content: '';
	display: inline-block;
	width: 8px;
	height: 8px;
	margin-right: 4px;
	border-radius: 2px;
	background: #93c5fd;
}

.seat-preview-dialog .legend-text {
	color: #94a3b8;
}

.seat-preview-dialog .legend-rule {
	flex: 1;
	min-width: 200px;
	color: #0369a1;
}

.seat-preview-dialog .seat-preview-warn {
	display: flex;
	align-items: center;
	gap: 6px;
	padding: 8px 12px;
	margin-bottom: 10px;
	border-radius: 6px;
	background: #fef2f2;
	border: 1px solid #fecaca;
	color: #dc2626;
	font-size: 12px;
}

.seat-preview-dialog .seat-preview-warn .el-icon-warning {
	font-size: 14px;
}

.seat-preview-dialog .seat-map-podium {
	text-align: center;
	font-size: 12px;
	color: #64748b;
	font-weight: 600;
	padding: 6px 0 10px;
	border-bottom: 2px dashed #cbd5e1;
	margin-bottom: 12px;
}

.seat-preview-dialog .seat-map-preview-area {
	min-height: 280px;
	max-height: 460px;
	padding: 16px;
}

.seat-preview-dialog .seat-layout-grid {
	display: grid;
	gap: 8px;
}

.seat-preview-dialog .seat-cell {
	min-height: 64px;
	border-radius: 8px;
	border: 1px solid #e2e8f0;
	background: #fff;
	padding: 6px 8px;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	text-align: center;
	transition: all 0.2s ease;
	user-select: none;
}

.seat-preview-dialog .seat-cell.is-empty {
	background: #f8fafc;
	border-style: dashed;
	color: #94a3b8;
}

.seat-preview-dialog .seat-cell.is-locked {
	background: #f1f5f9;
	border-color: #cbd5e1;
	color: #64748b;
	cursor: not-allowed;
}

.seat-preview-dialog .seat-cell.is-new {
	background: #eff6ff;
	border-color: #93c5fd;
	cursor: grab;
}

.seat-preview-dialog .seat-cell.is-new:active {
	cursor: grabbing;
}

.seat-preview-dialog .seat-cell.is-duplicate {
	border-color: #ef4444;
	background: #fef2f2;
	box-shadow: 0 0 0 1px rgba(239, 68, 68, 0.25);
}

.seat-preview-dialog .seat-cell-no {
	font-size: 11px;
	font-weight: 700;
	color: var(--btn-primary, #2563eb);
	margin-bottom: 2px;
}

.seat-preview-dialog .seat-cell-name {
	font-size: 12px;
	font-weight: 700;
	color: #1e293b;
	max-width: 100%;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.seat-preview-dialog .seat-cell-class {
	font-size: 10px;
	color: #94a3b8;
	max-width: 100%;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.seat-preview-dialog .seat-cell-empty {
	font-size: 11px;
	color: #cbd5e1;
}

.glass-modal .preview-student-grid::-webkit-scrollbar {
	width: 6px;
}

.glass-modal .preview-student-grid::-webkit-scrollbar-thumb {
	background-color: rgba(156, 163, 175, 0.45);
	border-radius: 3px;
}

.glass-modal .preview-student-tile {
	background: #f8fafc;
	border: 1px solid #e2e8f0;
	border-radius: 10px;
	padding: 10px 12px;
	display: flex;
	flex-direction: row; /* 水平左右布局：头像+信息在左，输入框在右 */
	align-items: center;
	justify-content: space-between;
	transition: all 0.25s ease;
	position: relative;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
	gap: 8px;
}

.glass-modal .preview-student-tile:hover {
	border-color: #3b82f6;
	background: #ffffff;
	box-shadow: 0 4px 12px rgba(59, 130, 246, 0.09);
	transform: translateY(-2px);
}

.glass-modal .preview-tile-left {
	display: flex;
	align-items: center;
	gap: 10px;
	min-width: 0;
	flex: 1;
}

.glass-modal .preview-tile-avatar {
	width: 32px;
	height: 32px;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 11px;
	font-weight: 700;
	box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.06);
	user-select: none;
	transition: transform 0.25s ease;
	flex-shrink: 0;
}

.glass-modal .preview-student-tile:hover .preview-tile-avatar {
	transform: scale(1.08);
}

.glass-modal .avatar-img {
	width: 100%;
	height: 100%;
	border-radius: 50%;
	object-fit: cover;
	display: block;
}

.glass-modal .preview-tile-meta {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	min-width: 0;
	flex: 1;
}

.glass-modal .preview-tile-name {
	font-size: 13px;
	font-weight: 700;
	color: #0f172a;
	margin-bottom: 2px;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	width: 100%;
	text-align: left;
}

.glass-modal .preview-tile-class {
	font-size: 11px;
	color: #64748b;
	margin-bottom: 0;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	width: 100%;
	text-align: left;
}

.glass-modal .preview-tile-input-box {
	display: flex;
	justify-content: center;
	align-items: center;
	flex-shrink: 0;
}

.glass-modal .preview-seat-input {
	width: 82px !important;
}

.glass-modal .preview-seat-input .el-input-number__decrease,
.glass-modal .preview-seat-input .el-input-number__increase {
	width: 18px;
	background: #f1f5f9;
	border-color: #e2e8f0;
}

.glass-modal .preview-seat-input .el-input__inner {
	padding-left: 2px;
	padding-right: 20px;
	height: 26px;
	line-height: 26px;
	font-weight: 700;
	color: #1e293b;
	border-radius: 6px;
	border-color: #e2e8f0;
}

.exam-lifecycle-alert {
	margin-bottom: 12px;
}

.session-form-dialog-header {
	padding-right: 28px;
}

.session-form-dialog-header__title {
	font-size: 18px;
	font-weight: 700;
	color: #1e293b;
	line-height: 1.3;
}

.session-form-dialog-header__range {
	display: flex;
	align-items: flex-start;
	gap: 6px;
	margin-top: 8px;
	padding: 8px 12px;
	background: #f8fafc;
	border: 1px solid #e2e8f0;
	border-radius: 8px;
	font-size: 12px;
	color: #64748b;
	line-height: 1.5;
}

.session-form-dialog-header__range i {
	margin-top: 2px;
	color: #94a3b8;
	flex-shrink: 0;
}

.session-form-body .el-form-item {
	margin-bottom: 18px;
}

.session-form-body .session-form-remark-item {
	margin-bottom: 0;
}

.signin-stats-row {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 8px;
	margin-bottom: 4px;
}

.exam-lifecycle-alert {
	margin-bottom: 12px;
}

.signin-stat-chip {
	display: inline-flex;
	align-items: center;
	height: 28px;
	padding: 0 12px;
	border-radius: 14px;
	font-size: 12px;
	font-weight: 600;
	cursor: pointer;
	user-select: none;
	border: 1px solid transparent;
	transition: all 0.15s ease;
}

.signin-stat-chip--total {
	background: #f1f5f9;
	color: #475569;
}

.signin-stat-chip--signed {
	background: #ecfdf5;
	color: #15803d;
}

.signin-stat-chip--unsigned {
	background: #fff7ed;
	color: #c2410c;
}

.signin-stat-chip--manual {
	background: #eff6ff;
	color: #1d4ed8;
}

.signin-stat-chip.is-active {
	box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.25);
}

.signin-stat-chip--total.is-active {
	border-color: #94a3b8;
	background: #e2e8f0;
}

.signin-stat-chip--signed.is-active {
	border-color: #22c55e;
	background: #dcfce7;
}

.signin-stat-chip--unsigned.is-active {
	border-color: #f97316;
	background: #ffedd5;
}

.signin-stat-chip--manual.is-active {
	border-color: #3b82f6;
	background: #dbeafe;
}

.signin-filter-row {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 10px;
	margin: 12px 0;
}

.signin-filter-label {
	font-size: 13px;
	color: #64748b;
}

.signin-pagination-row {
	display: flex;
	justify-content: flex-end;
	margin-top: 12px;
}

.print-seat-preview {
	padding: 8px 4px 16px;
}

.print-seat-title {
	text-align: center;
	font-size: 18px;
	margin: 0 0 8px;
	color: #0f172a;
}

.print-seat-meta {
	text-align: center;
	font-size: 14px;
	color: #475569;
	margin-bottom: 16px;
}

.print-seat-grid {
	display: grid;
	gap: 8px;
}

.print-seat-cell {
	border: 1px solid #cbd5e1;
	border-radius: 6px;
	padding: 8px 6px;
	min-height: 64px;
	text-align: center;
	background: #f8fafc;
}

.print-seat-no {
	font-weight: 700;
	font-size: 14px;
	color: #1e293b;
}

.print-seat-name {
	margin-top: 6px;
	font-size: 13px;
	color: #0f172a;
}

.print-seat-code {
	font-size: 11px;
	color: #64748b;
	margin-top: 2px;
}

.print-seat-cell .empty-seat {
	color: #94a3b8;
	margin-top: 10px;
	font-size: 12px;
}

.glass-modal .preview-seat-input .el-input__inner:focus {
	border-color: #3b82f6;
}

/* 重复座位号的冲突警示 */
.glass-modal .preview-student-tile.is-duplicate {
	border-color: #f87171 !important;
	background: #fef2f2 !important;
	box-shadow: 0 0 0 1px #f87171;
}

.glass-modal .preview-student-tile.is-duplicate .preview-tile-name {
	color: #dc2626;
}

.glass-modal .preview-student-tile.is-duplicate .preview-seat-input .el-input__inner {
	border-color: #f87171 !important;
	color: #dc2626 !important;
	background-color: #fffbeb !important;
}

/* 考试管理列表应用人员范围查看按钮与弹窗。最后更新：2026-09-08 */
.kq-dept-link-btn {
	padding: 4px 10px;
	font-size: 13px;
	color: #2563eb;
	background-color: #eff6ff;
	border-radius: 4px;
	line-height: 18px;
	border: 1px solid #bfdbfe;
	display: inline-flex;
	align-items: center;
	gap: 4px;
	cursor: pointer;
	transition: all 0.2s;
}

.kq-dept-link-btn:hover,
.kq-dept-link-btn:focus {
	color: #1d4ed8;
	background-color: #dbeafe;
	border-color: #93c5fd;
}

.ac-empty-dash {
	color: #9ca3af;
	font-size: 14px;
}

.kq-dept-view-dialog .el-dialog__body {
	padding: 16px 20px 10px;
}

.kq-view-head {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 16px;
	background-color: #f8fafc;
	border: 1px solid #e2e8f0;
	border-radius: 6px;
	padding: 10px 16px;
	margin-bottom: 14px;
}

.kq-view-head-item {
	font-size: 13px;
	color: #334155;
}

.kq-view-head-item .label {
	color: #64748b;
}

.kq-view-head-item .val {
	font-weight: 500;
	color: #0f172a;
}

.kq-view-head-item .count-badge {
	display: inline-block;
	padding: 1px 8px;
	border-radius: 12px;
	font-size: 12px;
	background-color: #eff6ff;
	color: #2563eb;
	font-weight: 600;
}

.kq-view-toolbar {
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 12px;
	margin-bottom: 12px;
}

.kq-view-search-input {
	max-width: 280px;
}

.kq-view-table-wrap {
	border-radius: 4px;
	overflow: hidden;
}

.kq-view-table .dept-text {
	font-size: 13px;
	color: #1e293b;
	font-weight: 500;
}

.kq-view-empty {
	padding: 30px 0;
	color: #94a3b8;
	font-size: 13px;
}
</style>